import React, { useState, useEffect, useRef } from 'react';
import PhoneNumberInput from './phoneNumberInput';
import OrderCompleted from './orderCompleted';
import { placeDemoOrder } from '../utils/demoOrderStore';
import ReviewBenefits from './reviewBenefits';
import ReviewGuide from './reviewGuide';
import { lockScroll, unlockScroll } from '../utils/scrollbarUtils';
import { formatOrderForUnion, sendOrderToUnion, checkTableAvailability, fetchTableOrderDetail, checkTableOrderExists as checkTableOrderExistsUnion } from '../utils/unionApi';
import { formatOrderForOKDC, sendOrderToOKDC, checkTableOrderExists as checkTableOrderExistsOKDC, fetchOKDCTableOrderNumber } from '../utils/okdcApi';
import { formatOrderForMetacity, sendOrderToMetacity } from '../utils/metacityApi';
import { sendOrderToWowpos, fetchWowposOrderHistory } from '../utils/wowposApi';
import { simApi } from '../utils/simApi';
import wsService from '../utils/websocket';
import refuseIcon from '../assets/icons/common/refuse_icon.png';
import trashIcon from '../assets/icons/common/trash_icon.png';
import cartIcon from '../assets/icons/common/cart_icon.png';
import cartNotActiveIcon from '../assets/icons/common/cartnotactiveicon.png';
import bottomNavBg from '../assets/icons/common/bottomNav.png';

// 데모(체험) 모드 — 백엔드 없이 허구 주문
const IS_DEMO = true;
const formatDemoItems = (cartItems) => (cartItems || []).map((item) => {
  const basePrice = item.menuItem?.basePrice || item.menuItem?.price || item.price || 0;
  const qty = item.quantity || 1;
  const opts = [];
  const pushOpt = (o) => {
    if (o && typeof o === 'object' && (o.name || o.ko?.name || o.option_name_ko)) {
      const onKo = o.option_name_ko || o.ko?.name || o.name || '';
      opts.push({
        name: onKo,
        name_ko: onKo,
        name_en: o.option_name_en || o.en?.name || o.name_en || onKo,
        name_ja: o.option_name_ja || o.ja?.name || o.name_ja || onKo,
        name_zh: o.option_name_zh || o.zh?.name || o.name_zh || onKo,
        quantity: o.quantity || 1,
        price: parseInt(o.option_price || o.additional_price || o.price || 0) || 0
      });
    }
  };
  const so = item.selectedOptions;
  if (Array.isArray(so)) so.forEach(pushOpt);
  else if (so && typeof so === 'object') Object.values(so).forEach((v) => (Array.isArray(v) ? v.forEach(pushOpt) : pushOpt(v)));
  const optSum = opts.reduce((s, o) => s + (Number(o.price) || 0) * (Number(o.quantity) || 1), 0);
  const mi = item.menuItem || {};
  const name = mi.name_ko || mi.ko?.name || mi.name || mi.menu_name || '';
  const nameEn = mi.name_en || mi.en?.name || name;
  const nameJa = mi.name_ja || mi.ja?.name || name;
  const nameZh = mi.name_zh || mi.zh?.name || name;
  return {
    name, name_ko: name, name_en: nameEn, name_ja: nameJa, name_zh: nameZh,
    price: basePrice + optSum,           // 단가(옵션 포함) — 렌더가 × quantity
    totalPrice: basePrice + optSum,      // 단가 (렌더에서 × quantity 로 라인합계)
    quantity: qty,
    itemCode: item.menuItem?.id || item.menuItem?.prod_cd || '',
    selectedOptions: opts,
    // 사은품 판별은 표식으로만 한다. id/menu_code/prod_cd 값 비교는
    // 정규 메뉴와 우연히 겹칠 수 있어 유료 메뉴가 사은품으로 표시될 수 있다.
    is_review_benefit: item.menuItem?.is_review_benefit === true,
  };
});

const CartDrawer = ({ isOpen, onClose, onOrderComplete, onRequestScrollToCategories, onShowToast, selectedLanguage = 'ko', tableCode = '001', posType: propPosType = null, storeId: propStoreId = null, sessionToken = null, reviewSettings: propReviewSettings = null, hasExistingOrder: propHasExistingOrder = false, isSimulation = false }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [userId, setUserId] = useState(null);
  const [isPhoneInputOpen, setIsPhoneInputOpen] = useState(false);
  const [isOrderCompletedOpen, setIsOrderCompletedOpen] = useState(false);
  const [isOrdering, setIsOrdering] = useState(false);
  const [isReviewBenefitsOpen, setIsReviewBenefitsOpen] = useState(false);
  const [isReviewGuideOpen, setIsReviewGuideOpen] = useState(false);
  const [orderNumber, setOrderNumber] = useState(null);
  // const [, setShouldOpenOrderHistory] = useState(false); // 사용하지 않음
  const [selectedBenefit, setSelectedBenefit] = useState(null);
  const [willWriteReview, setWillWriteReview] = useState(false);
  const broadcastChannelRef = useRef(null);
  const [reviewEnabled, setReviewEnabled] = useState(false);
  const [posType, setPosType] = useState(propPosType);
  const [storeId, setStoreId] = useState(propStoreId);

  // Props(세션 데이터)가 변경되면 state 업데이트
  useEffect(() => {
    if (propPosType) {
      console.log('[CartDrawer] ✅ 세션에서 posType 설정:', propPosType);
      setPosType(propPosType);
    }
    if (propStoreId) {
      console.log('[CartDrawer] ✅ 세션에서 storeId 설정:', propStoreId);
      setStoreId(propStoreId);
    }
  }, [propPosType, propStoreId]);

  // Check if review is enabled (props 우선, localStorage 폴백)
  useEffect(() => {
    if (propReviewSettings) {
      // Props로 받은 설정 사용 (DB에서 로드된 값)
      console.log('[CartDrawer] ✅ Props에서 리뷰 설정 로드:', propReviewSettings);
      setReviewEnabled(propReviewSettings.isEnabled || false);
    } else {
      // Props 없으면 localStorage 폴백
      const reviewSettings = localStorage.getItem('reviewSettings');
      if (reviewSettings) {
        try {
          const settings = JSON.parse(reviewSettings);
          console.log('[CartDrawer] ⚠️  localStorage에서 리뷰 설정 폴백:', settings);
          setReviewEnabled(settings.isEnabled || false);
        } catch (error) {
          console.error('[CartDrawer] Failed to parse review settings:', error);
          setReviewEnabled(false);
        }
      } else {
        setReviewEnabled(false);
      }
    }
  }, [isOpen, propReviewSettings]); // Props 변경 시에도 업데이트
  
  // 바로 주문 처리 함수
  const handleDirectOrder = async () => {
    if (isOrdering) return;
    setIsOrdering(true);

    // POS 타입 확인
    if (!posType) {
      console.error('[주문 처리] ❌ POS 타입을 알 수 없습니다.');
      return;
    }

    // 데모(체험) 모드: 허구 주문 저장 + 허구 주문번호 + 주문완료
    if (IS_DEMO) {
      try {
        const order = placeDemoOrder(formatDemoItems(cartItems), { isReview: false });
        setOrderNumber(order.orderNumber);
        localStorage.removeItem('cartData');
        setCartItems([]);
        setIsOrderCompletedOpen(true);
      } catch (err) {
        if (onShowToast) onShowToast('주문 처리 중 오류가 발생했습니다', 'error');
      } finally {
        setIsOrdering(false);
      }
      return;
    }

    console.log('[주문 처리] ==============================================');
    console.log(`[주문 처리] 📋 현재 POS 타입: "${posType}"`);
    console.log(`[주문 처리] 📋 매장 ID: ${storeId}`);
    console.log(`[주문 처리] 📋 테이블 코드: ${tableCode}`);
    console.log('[주문 처리] ==============================================');

    // OKDC POS인 경우
    if (posType === 'okdc') {
      console.log('[주문 처리] ✅ OKDC 주문 경로로 진입');
      return await handleOKDCOrder();
    }

    // WowPos (비연동 POS) — 우리 백엔드 → 매장 PC 클라이언트 WS 송신
    if (posType === 'wowpos') {
      console.log('[주문 처리] ✅ WowPos 주문 경로로 진입');
      try {
        const items = cartItems.map((item) => {
          const basePrice = item.menuItem?.basePrice || item.menuItem?.price || 0;
          const qty = item.quantity || 1;
          // 선택 옵션 추출 (WowPos: 단일=객체값 / 다중=배열 값 혼재 가능)
          const optList = [];
          const pushOpt = (o) => {
            if (o && typeof o === 'object' && (o.name || o.ko?.name)) {
              optList.push({
                name: o.ko?.name || o.name,
                qty: o.quantity || 1,
                price: parseInt(o.option_price || o.additional_price || o.price || 0) || 0,
              });
            }
          };
          const so = item.selectedOptions;
          if (Array.isArray(so)) so.forEach(pushOpt);
          else if (so && typeof so === 'object') {
            Object.values(so).forEach((v) => { if (Array.isArray(v)) v.forEach(pushOpt); else pushOpt(v); });
          }
          // 옵션 추가금 포함 단가 (라인 합계 = (기본 + 옵션합) × 수량)
          const optionsPrice = optList.reduce((s, o) => s + (Number(o.price) || 0) * (Number(o.qty) || 1), 0);
          return {
            menuId: item.menuItem?.db_id || item.menuItem?.id || null,
            name: item.menuItem?.ko?.name || item.menuItem?.name || '',
            price: (basePrice + optionsPrice) * qty,
            quantity: qty,
            options: optList,
          };
        });
        const totalAmount = items.reduce((s, i) => s + (Number(i.price) || 0), 0);

        const result = await sendOrderToWowpos({
          storeId: propStoreId,
          tableCode,
          items,
          totalAmount,
        });

        if (result.success) {
          // 와우포스: POST 응답 ack 안에 PC OrderCounter 발급분(orderNumber)이 들어옴.
          // Union/OKDC 와 동일한 UX — 카트 내부 OrderCompleted 페이지 표시 + 사용자가 닫을 때 onClose/onOrderComplete.
          setOrderNumber(result.orderNumber || null);

          // 장바구니 비우기 (OKDC/Union 와 동일)
          localStorage.removeItem('cartData');
          setCartItems([]);
          if (wsService.isSocketConnected()) wsService.syncCart([]);
          if (broadcastChannelRef.current) {
            broadcastChannelRef.current.postMessage({ type: 'cart-cleared' });
          }

          setIsOrderCompletedOpen(true);
        } else {
          if (onShowToast) onShowToast(result.error || '주문 전송 실패', 'error');
        }
      } catch (err) {
        console.error('[WowPos 주문] 오류:', err);
        if (onShowToast) onShowToast(err.message || '주문 전송 실패', 'error');
      } finally {
        setIsOrdering(false);
      }
      return;
    }

    // METACITY POS — 우리 백엔드 → 매장 PC 클라이언트 WS → 로컬 HTTP(8090) 8020 후불 주문
    if (posType === 'metacity') {
      console.log('[주문 처리] ✅ METACITY 주문 경로로 진입');
      try {
        const orderData = formatOrderForMetacity(cartItems, tableCode);
        const result = await sendOrderToMetacity(orderData, propStoreId, []);
        if (result.success) {
          setOrderNumber(result.orderNumber || null);
          localStorage.removeItem('cartData');
          setCartItems([]);
          if (wsService.isSocketConnected()) wsService.syncCart([]);
          if (broadcastChannelRef.current) {
            broadcastChannelRef.current.postMessage({ type: 'cart-cleared' });
          }
          setIsOrderCompletedOpen(true);
        } else {
          if (onShowToast) onShowToast(result.message || '주문 전송 실패', 'error');
        }
      } catch (err) {
        console.error('[METACITY 주문] 오류:', err);
        if (onShowToast) onShowToast(err.message || '주문 전송 실패', 'error');
      } finally {
        setIsOrdering(false);
      }
      return;
    }

    // Union POS인 경우 (기존 로직)
    console.log('[주문 처리] ✅ Union POS 주문 경로로 진입');
    // 테이블 사용 가능 여부 체크
    try {
      const tableCheck = isSimulation
        ? await simApi.checkTableAvailability()
        : await checkTableAvailability(tableCode);
      
      if (tableCheck.tableUse !== 'N') {
        // 테이블 사용 불가 (Y: 사용중, E: 테이블없음)
        console.log('테이블 사용 불가:', tableCheck.tableUse);
        
        // 애니메이션으로 페이지 닫기
        setIsAnimating(false);
        
        // 토스트 메시지 먼저 예약
        if (onShowToast) {
          setTimeout(() => {
            const message = selectedLanguage === 'ko' ? '다시 시도해주세요' :
                          selectedLanguage === 'en' ? 'Please try again' :
                          selectedLanguage === 'ja' ? 'もう一度お試しください' :
                          '请重试';
            onShowToast(`<img src="${refuseIcon}" style="width: 12px; height: 12px; vertical-align: middle; margin-right: 8px;" alt="x"/><span style="color: white; font-weight: 600; font-size: 14px;">${message}</span>`);
          }, 200); // 페이지 닫기 시작과 거의 동시에
        }
        
        setTimeout(() => {
          setIsVisible(false);
          unlockScroll();
          onClose();
        }, 300); // 슬라이드 다운 애니메이션 시간
        
        return;
      }
    } catch (error) {
      console.error('테이블 체크 실패:', error);
      
      // 애니메이션으로 페이지 닫기
      setIsAnimating(false);
      
      // 토스트 메시지 먼저 예약
      if (onShowToast) {
        setTimeout(() => {
          const message = selectedLanguage === 'ko' ? '다시 시도해주세요' :
                        selectedLanguage === 'en' ? 'Please try again' :
                        selectedLanguage === 'ja' ? 'もう一度お試しください' :
                        '请重试';
          onShowToast(`<img src="${refuseIcon}" style="width: 12px; height: 12px; vertical-align: middle; margin-right: 8px;" alt="x"/><span style="color: white; font-weight: 600; font-size: 14px;">${message}</span>`);
        }, 200);
      }
      
      setTimeout(() => {
        setIsVisible(false);
        unlockScroll();
        onClose();
      }, 300);
      
      return;
    }
    
    // Union POS API로 주문 전송
    let newOrderNumber = null;
    try {
      // 장바구니 데이터를 Union API 형식으로 변환
      console.log('========== Union API 주문 변환 시작 ==========');
      console.log('cartItems:', cartItems);

      const unionOrderData = formatOrderForUnion(
        cartItems.map(item => {
          console.log('처리 중인 아이템:', item);
          console.log('isFromChoiceMenu:', item.isFromChoiceMenu);
          return {
            item: item.menuItem,
            quantity: item.quantity,
            selectedOptions: item.selectedOptions || {},
            isFromChoiceMenu: item.isFromChoiceMenu // 선택 메뉴 플래그 전달
          };
        }),
        tableCode, // 세션에서 전달받은 테이블 코드 사용
        selectedLanguage === 'ko' ? 'KO' :
        selectedLanguage === 'en' ? 'EN' :
        selectedLanguage === 'ja' ? 'JA' : 'ZH'
      );

      console.log('[Union 주문] CartDrawer prop tableCode:', tableCode);
      console.log('[Union 주문] unionOrderData.TableCode:', unionOrderData.TableCode);

      // 알림용 데이터 구성 (한국어 메뉴명 우선, 수량, 가격, 옵션)
      const notificationItems = cartItems.map(item => {
        const options = [];
        const menuType = item.menuItem?.menuType;
        if (menuType === '고정세트') {
          // 고정세트: menuItem.options[].choices[]에서 구성품 추출
          if (Array.isArray(item.menuItem?.options)) {
            item.menuItem.options.forEach(optGroup => {
              if (optGroup.choices && Array.isArray(optGroup.choices)) {
                optGroup.choices.forEach(choice => {
                  if (choice.name || choice.ko?.name) {
                    options.push({ name: choice.ko?.name || choice.name, qty: choice.quantity || 1 });
                  }
                });
              }
            });
          }
        } else if (item.selectedOptions) {
          // 일반 메뉴/변동세트: selectedOptions에서 추출
          if (Array.isArray(item.selectedOptions)) {
            item.selectedOptions.forEach(opt => {
              if (opt && opt.name) {
                options.push({ name: opt.ko?.name || opt.name, qty: opt.quantity || 1 });
              }
            });
          } else if (typeof item.selectedOptions === 'object') {
            Object.values(item.selectedOptions).forEach(choice => {
              if (choice && typeof choice === 'object' && choice.name) {
                options.push({ name: choice.ko?.name || choice.name, qty: choice.quantity || 1 });
              }
            });
          }
        }
        const basePrice = item.menuItem?.ko?.price || item.menuItem?.price || item.menuItem?.basePrice || 0;
        let optionsPrice = 0;
        if (Array.isArray(item.selectedOptions)) {
          optionsPrice = item.selectedOptions.reduce((sum, opt) => sum + (parseInt(opt?.option_price || opt?.price || 0) || 0), 0);
        } else if (item.selectedOptions && typeof item.selectedOptions === 'object') {
          optionsPrice = Object.values(item.selectedOptions).reduce((sum, opt) => sum + (parseInt(opt?.option_price || opt?.price || 0) || 0), 0);
        }
        return {
          name: item.menuItem?.ko?.name || item.menuItem?.name || '',
          qty: item.quantity || 1,
          price: (basePrice + optionsPrice) * (item.quantity || 1),
          options
        };
      });
      const notificationTotalPrice = notificationItems.reduce((sum, i) => sum + i.price, 0);

      // API 호출 — 시뮬 매장은 /api/simulation/order, 일반은 Union API
      const result = isSimulation
        ? await simApi.sendOrder({
            hasOrder: propHasExistingOrder === true,
            notificationItems,
            notificationTotalPrice,
          })
        : await sendOrderToUnion(unionOrderData, notificationItems, notificationTotalPrice);

      if (result.success) {
        // 주문 성공 후 주문 번호 조회 — 시뮬 매장은 스킵
        try {
          const orderDetail = isSimulation
            ? { success: true, orderSum: null }
            : await fetchTableOrderDetail(tableCode);
          if (orderDetail.success && orderDetail.orderSum) {
            newOrderNumber = orderDetail.orderSum.OrderNo;
            console.log('[Union 주문] 주문 전송 후 주문 번호 조회:', newOrderNumber);
          } else {
            newOrderNumber = 1;
            console.log('[Union 주문] 주문 번호 조회 실패, 기본값 사용:', newOrderNumber);
          }
        } catch (orderDetailError) {
          console.error('[Union 주문] 주문 번호 조회 오류:', orderDetailError);
          newOrderNumber = 1;
        }
      } else {
        console.error('주문 전송 실패:', result.message);
        newOrderNumber = 1;
      }
    } catch (error) {
      console.error('주문 전송 중 오류:', error);
      newOrderNumber = 1;
    }
    setOrderNumber(newOrderNumber);
    
    // 주문 데이터 생성 (로컬 저장용)
    const orderData = {
      orderNumber: newOrderNumber,
      timestamp: new Date().toISOString(),
      phoneNumber: '0000', // 리뷰 기능 OFF일 때는 전화번호 없음
      items: cartItems.map(item => {
        // 선택 메뉴에서 생성된 항목인 경우 (이미 독립적인 메뉴로 변환됨)
        if (item.isFromChoiceMenu) {
          const price = item.menuItem.price || item.menuItem.basePrice || 0;
          return {
            name: item.menuItem.name || item.menuItem.ko?.name,
            basePrice: price,
            totalPrice: price,
            quantity: item.quantity,
            hasOptions: false,
            selectedOptions: [],
            options: null,
            menuType: null,
            itemCode: item.menuItem.itemCode
          };
        }

        // 선택 메뉴 타입 체크 (구버전 호환)
        const isChoiceType = item.menuItem?.menuType === '선택';

        // 기본 가격 파싱
        let basePrice = 0;
        let itemName = item.menuItem[selectedLanguage]?.name || item.menuItem.ko?.name || item.menuItem.name;
        let itemCode = item.menuItem?.itemCode;

        // 선택 메뉴인 경우 선택한 옵션의 정보로 대체 (구버전 호환)
        if (isChoiceType && item.selectedOptions) {
          // 선택된 옵션 찾기 (배열에서 첫 번째 요소가 선택한 옵션)
          const selectedOption = Array.isArray(item.selectedOptions)
            ? item.selectedOptions[0]
            : Object.values(item.selectedOptions)[0];

          if (selectedOption) {
            // 선택한 옵션의 가격을 기본 가격으로 설정
            basePrice = parseInt(selectedOption.price?.toString().replace(/[^0-9]/g, '') || '0');
            // 선택한 옵션의 이름을 사용
            itemName = selectedOption.name || itemName;
            // 선택한 옵션의 itemCode를 사용
            itemCode = selectedOption.itemCode || itemCode;
          }
        } else {
          // 일반 메뉴나 세트 메뉴의 경우 기존 로직 유지
          if (item.menuItem) {
            const priceValue = item.menuItem[selectedLanguage]?.price || item.menuItem.ko?.price || item.menuItem.price || item.menuItem.basePrice || 0;
            // price가 숫자인 경우와 문자열인 경우 모두 처리
            if (typeof priceValue === 'number') {
              basePrice = priceValue;
            } else if (typeof priceValue === 'string') {
              basePrice = parseInt(priceValue.replace(/[^0-9]/g, '')) || 0;
            }
          }
        }

        // 옵션 가격 계산 (선택 메뉴가 아닌 경우만)
        let optionsPrice = 0;
        let hasOptions = false;
        if (!isChoiceType && item.selectedOptions) {
          // 새 형식 (배열) 체크
          if (Array.isArray(item.menuItem.options)) {
            Object.entries(item.selectedOptions).forEach(([key, choice]) => {
              if (choice && choice.price) {
                const price = parseInt(choice.price.toString().replace(/[^0-9]/g, '')) || 0;
                optionsPrice += price;
                hasOptions = true;
              }
            });
          }
          // 기존 형식 (객체)
          else if (item.menuItem.options) {
            Object.entries(item.menuItem.options).forEach(([optionTitle, optionData], index) => {
              const selected = item.selectedOptions[index];

              if (optionData.type === 'required' && selected) {
                const option = optionData.items.find(opt => opt.name === selected);
                if (option && option.price) {
                  optionsPrice += option.price;
                  hasOptions = true;
                }
              } else if (optionData.type === 'optional' && Array.isArray(selected)) {
                selected.forEach(optionName => {
                  const option = optionData.items.find(opt => opt.name === optionName);
                  if (option && option.price) {
                    optionsPrice += option.price;
                    hasOptions = true;
                  }
                });
              }
            });
          }
        }

        return {
          name: itemName,
          // 다국어 메뉴 이름 필드 추가
          name_ko: item.menuItem.name_ko || item.menuItem.menu_name || itemName,
          name_en: item.menuItem.name_en || item.menuItem.menu_name || itemName,
          name_ja: item.menuItem.name_ja || item.menuItem.menu_name || itemName,
          name_zh: item.menuItem.name_zh || item.menuItem.menu_name || itemName,
          basePrice: basePrice,
          totalPrice: basePrice + optionsPrice,
          quantity: item.quantity,
          hasOptions: hasOptions,
          selectedOptions: item.selectedOptions,
          options: item.menuItem.options,
          menuType: item.menuItem?.menuType,
          itemCode: itemCode
        };
      })
    };
    
    // 기존 주문 내역 가져오기
    const existingOrders = JSON.parse(localStorage.getItem('orderHistory') || '[]');

    // 같은 주문번호가 있는지 확인
    const existingOrderIndex = existingOrders.findIndex(order => order.orderNumber === newOrderNumber);

    if (existingOrderIndex >= 0) {
      // 같은 주문번호가 있으면 해당 주문에 메뉴 추가
      const existingOrder = existingOrders[existingOrderIndex];

      // 새로운 메뉴들을 기존 주문에 추가 (같은 메뉴는 수량 증가)
      orderData.items.forEach(newItem => {
        // 같은 메뉴 찾기 (이름과 옵션이 같은지 확인)
        const existingItemIndex = existingOrder.items.findIndex(item => {
          // 기본적으로 이름과 itemCode로 비교
          if (item.name === newItem.name && item.itemCode === newItem.itemCode) {
            // 옵션도 같은지 확인
            const existingOptions = JSON.stringify(item.selectedOptions || []);
            const newOptions = JSON.stringify(newItem.selectedOptions || []);
            return existingOptions === newOptions;
          }
          return false;
        });

        if (existingItemIndex >= 0) {
          // 같은 메뉴가 있으면 수량 증가
          existingOrder.items[existingItemIndex].quantity += newItem.quantity;
        } else {
          // 새로운 메뉴면 추가
          existingOrder.items.push(newItem);
        }
      });

      // 주문 시간 업데이트
      existingOrder.timestamp = new Date().toISOString();

    } else {
      // 새로운 주문번호면 새로운 주문으로 추가
      existingOrders.push(orderData);
    }

    // localStorage 사용 중단 - Union API에서만 주문내역 조회
    // localStorage.setItem('orderHistory', JSON.stringify(existingOrders));
    
    // 즉시 장바구니 비우기
    localStorage.removeItem('cartData');
    setCartItems([]);
    
    // BroadcastChannel로 다른 탭에도 알림
    if (broadcastChannelRef.current) {
      broadcastChannelRef.current.postMessage({ type: 'cart-cleared' });
    }
    
    // WebSocket으로 다른 멤버들에게 주문 완료 알림
    if (wsService.isSocketConnected()) {
      wsService.emitOrderCompleted();
    }

    // 화면 전환 (사용자가 "주문판으로 돌아가기" 버튼을 누를 때까지 유지)
    setIsOrderCompletedOpen(true);
  };

  // OKDC 주문 처리 함수
  const handleOKDCOrder = async () => {
    console.log('[OKDC 주문] 주문 처리 시작');

    try {
      // OKDC 주문 데이터 포맷 변환 (await 추가)
      const okdcOrderData = await formatOrderForOKDC(
        cartItems.map(item => ({
          menuId: item.menuItem.id,
          prod_cd: item.menuItem.prod_cd || item.menuItem.menu_code,
          price: item.menuItem.price || item.menuItem.basePrice,
          quantity: item.quantity,
          selectedOptions: item.selectedOptions || [],
          is_taxable: item.menuItem.is_taxable || 'Y'
        })),
        tableCode, // 세션에서 전달받은 테이블 코드 사용
        { storeId }
      );

      console.log('[OKDC 주문] 사용할 테이블 코드:', tableCode);

      console.log('[OKDC 주문] 포맷 변환 완료:', okdcOrderData);

      // OKDC API로 주문 전송
      const result = await sendOrderToOKDC(okdcOrderData, storeId);

      if (result.success) {
        console.log('[OKDC 주문] 주문 성공:', result);

        // 주문 데이터 로컬 저장
        const orderData = {
          orderNumber: okdcOrderData.orderNo,
          timestamp: new Date().toISOString(),
          phoneNumber: '0000',
          items: cartItems.map(item => ({
            name: item.menuItem.name || item.menuItem.ko?.name || item.menuItem.prod_nm,
            basePrice: item.menuItem.price || item.menuItem.basePrice,
            totalPrice: (item.menuItem.price || item.menuItem.basePrice) * item.quantity,
            quantity: item.quantity,
            hasOptions: (item.selectedOptions && item.selectedOptions.length > 0) || false,
            selectedOptions: item.selectedOptions || []
          })),
          totalAmount: cartItems.reduce((total, item) => {
            let itemPrice = item.menuItem.price || item.menuItem.basePrice;
            if (item.selectedOptions && item.selectedOptions.length > 0) {
              item.selectedOptions.forEach(opt => {
                itemPrice += parseInt(opt.option_price || 0);
              });
            }
            return total + (itemPrice * item.quantity);
          }, 0),
          language: selectedLanguage,
          posType: 'okdc'
        };

        // 주문 내역 저장
        let orderHistory = JSON.parse(localStorage.getItem(`orderHistory_${userId}`) || '[]');
        orderHistory.push(orderData);
        localStorage.setItem(`orderHistory_${userId}`, JSON.stringify(orderHistory));

        // 장바구니 비우기
        localStorage.removeItem('cartData');
        setCartItems([]);

        // WebSocket으로 장바구니 비우기 전송 (다른 탭/기기 동기화)
        if (wsService.isSocketConnected()) {
          wsService.syncCart([]);
        }

        // BroadcastChannel로 다른 탭에도 알림
        if (broadcastChannelRef.current) {
          broadcastChannelRef.current.postMessage({ type: 'cart-cleared' });
        }

        // 주문 후 실제 주문번호 조회
        console.log('[OKDC 주문] 주문번호 조회 시작');
        const orderNumberResult = await fetchOKDCTableOrderNumber(storeId, tableCode);
        const finalOrderNumber = orderNumberResult.orderNumber || okdcOrderData.orderNo;

        console.log('[OKDC 주문] 최종 주문번호:', finalOrderNumber);
        setOrderNumber(finalOrderNumber);

        // 주문 완료 페이지 열기 (사용자가 "주문판으로 돌아가기" 버튼을 누를 때까지 유지)
        setIsOrderCompletedOpen(true);

      } else {
        console.error('[OKDC 주문] 주문 실패:', result.message);

        // 실패 메시지 표시
        setIsAnimating(false);
        setTimeout(() => {
          setIsVisible(false);
          unlockScroll();
          onClose();
        }, 300);

        if (onShowToast) {
          setTimeout(() => {
            const message = selectedLanguage === 'ko' ? `주문 실패: ${result.message}` :
                          selectedLanguage === 'en' ? `Order failed: ${result.message}` :
                          selectedLanguage === 'ja' ? `注文失敗: ${result.message}` :
                          `订单失败: ${result.message}`;
            onShowToast(`<img src="${refuseIcon}" style="width: 12px; height: 12px; vertical-align: middle; margin-right: 8px;" alt="x"/><span style="color: white; font-weight: 600; font-size: 14px;">${message}</span>`);
          }, 200);
        }
      }
    } catch (error) {
      console.error('[OKDC 주문] 주문 중 오류:', error);

      // 오류 메시지 표시
      setIsAnimating(false);
      setTimeout(() => {
        setIsVisible(false);
        unlockScroll();
        onClose();
      }, 300);

      if (onShowToast) {
        setTimeout(() => {
          const message = selectedLanguage === 'ko' ? '주문 중 오류가 발생했습니다' :
                        selectedLanguage === 'en' ? 'An error occurred during order' :
                        selectedLanguage === 'ja' ? '注文中にエラーが発生しました' :
                        '订单过程中发生错误';
          onShowToast(`<img src="${refuseIcon}" style="width: 12px; height: 12px; vertical-align: middle; margin-right: 8px;" alt="x"/><span style="color: white; font-weight: 600; font-size: 14px;">${message}</span>`);
        }, 200);
      }
    }
  };

  // Translations
  const translations = {
    ko: {
      cartTitle: '장바구니',
      price: '가격',
      order: '주문하기',
      emptyCartText: '담은 메뉴가 없어요',
      addMenu: '메뉴 담기'
    },
    en: {
      cartTitle: 'Cart',
      price: 'Price',
      order: 'Order',
      emptyCartText: 'No items in cart yet',
      addMenu: 'Add to Cart'
    },
    ja: {
      cartTitle: 'カート',
      price: '価格',
      order: 'を注文する',
      emptyCartText: 'まだメニューがカートに入っていません',
      addMenu: 'カートに入れる'
    },
    zh: {
      cartTitle: '购物车',
      price: '价格',
      order: '下单',
      emptyCartText: '购物车还没有菜品',
      addMenu: '加入购物车'
    }
  };
  
  const t = translations[selectedLanguage] || translations.ko;
  
  // 룰렛 애니메이션 상태
  const [displayPrice, setDisplayPrice] = useState('0');
  const [prevPrice, setPrevPrice] = useState('0');
  const [priceAnimating, setPriceAnimating] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  const [isPriceIncreasing, setIsPriceIncreasing] = useState(true);
  const priceAnimationTimeoutRef = useRef(null);
  
  // 수량 애니메이션 상태
  const [displayQuantity, setDisplayQuantity] = useState(0);
  const [prevQuantity, setPrevQuantity] = useState(0);
  const [quantityAnimating, setQuantityAnimating] = useState(false);
  const [quantityAnimationKey, setQuantityAnimationKey] = useState(0);
  const [isQuantityIncreasing, setIsQuantityIncreasing] = useState(true);
  
  // 삭제 애니메이션 상태
  const [removingItems, setRemovingItems] = useState(new Set());
  // const [, setAnimatingItems] = useState(new Set()); // 사용하지 않음

  // 사용자 ID 생성 또는 가져오기
  useEffect(() => {
    let storedUserId = localStorage.getItem('userId');
    if (!storedUserId) {
      storedUserId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('userId', storedUserId);
    }
    setUserId(storedUserId);
    
    // BroadcastChannel 생성 (같은 origin의 모든 탭/창에서 통신 가능)
    try {
      broadcastChannelRef.current = new BroadcastChannel('cart_channel');
      
      // 다른 탭에서 보낸 메시지 수신
      broadcastChannelRef.current.onmessage = (event) => {
        if (event.data.type === 'cart_updated') {
          // 다른 탭에서 장바구니가 업데이트되면 자동으로 새로고침
          // setTimeout으로 약간의 지연을 주어 localStorage가 업데이트된 후에 읽도록 함
          setTimeout(() => {
            loadCartData();
          }, 50);
        } else if (event.data.type === 'cart-cleared') {
          // 다른 탭에서 장바구니가 비워졌을 때
          localStorage.removeItem('cartData');
          setCartItems([]);
        }
      };
    } catch (error) {
      console.log('BroadcastChannel not supported:', error);
    }
    
    return () => {
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.close();
      }
      // 가격 애니메이션 타이머 정리
      if (priceAnimationTimeoutRef.current) {
        clearTimeout(priceAnimationTimeoutRef.current);
      }
    };
  }, []);

  // 장바구니 데이터 로드
  useEffect(() => {
    if (userId) {
      loadCartData();
    }
  }, [userId]);

  // WebSocket 리스너 설정 - 장바구니 실시간 동기화 (항상 활성화)
  useEffect(() => {
    console.log('[CartDrawer] 컴포넌트 마운트 - WebSocket 리스너 등록 시작');

    const handleCartUpdate = (updatedCartData) => {
      console.log('[CartDrawer] WebSocket에서 장바구니 업데이트 수신:', updatedCartData);
      console.log('[CartDrawer] 현재 열린 상태:', isOpen);

      // localStorage와 state 업데이트
      localStorage.setItem('cartData', JSON.stringify(updatedCartData));

      // 직접 상태 업데이트 (애니메이션 효과를 위해)
      setCartItems(updatedCartData);
      console.log('[CartDrawer] 장바구니 상태 업데이트 완료');
    };

    // 리스너 등록
    console.log('[CartDrawer] onCartUpdated 호출 전');
    wsService.onCartUpdated(handleCartUpdate);
    console.log('[CartDrawer] onCartUpdated 호출 완료');

    // cleanup - 컴포넌트 언마운트 시 특정 리스너만 해제
    return () => {
      // 특정 콜백만 제거 (다른 컴포넌트의 리스너는 유지)
      wsService.offCartUpdated(handleCartUpdate);
    };
  }, []); // 컴포넌트 마운트 시 한 번만 실행

  const loadCartData = () => {
    const cartData = JSON.parse(localStorage.getItem('cartData') || '[]');
    
    // 데이터가 실제로 변경되었는지 확인
    const isCartChanged = JSON.stringify(cartData) !== JSON.stringify(cartItems);
    
    // 변경된 경우에만 상태 업데이트
    if (isCartChanged) {
      setCartItems(cartData);
    }
  };


  // 수량 변경
  const updateQuantity = (itemId, newQuantity) => {
    console.log('[CartDrawer] updateQuantity 호출:', { itemId, newQuantity });
    // 전체 장바구니 데이터 가져오기
    const allCartData = JSON.parse(localStorage.getItem('cartData') || '[]');

    if (newQuantity < 1) {
      // 삭제 애니메이션 시작
      setRemovingItems(prev => new Set(prev).add(itemId));

      // 애니메이션 후 실제 삭제
      setTimeout(() => {
        const updatedCart = allCartData.filter(item => item.id !== itemId);
        localStorage.setItem('cartData', JSON.stringify(updatedCart));
        loadCartData();

        // 서버로 삭제 요청 (하이브리드)
        if (wsService.isSocketConnected()) {
          wsService.removeCartItem(itemId);
        }

        // 애니메이션 상태 정리
        setTimeout(() => {
          setRemovingItems(prev => {
            const newSet = new Set(prev);
            newSet.delete(itemId);
            return newSet;
          });
        }, 45);
      }, 180);
    } else {
      // 수량 업데이트
      const updatedCart = allCartData.map(item =>
        item.id === itemId ? { ...item, quantity: newQuantity } : item
      );
      localStorage.setItem('cartData', JSON.stringify(updatedCart));
      loadCartData();

      // 서버로 수량 변경 요청 (하이브리드)
      if (wsService.isSocketConnected()) {
        console.log('[CartDrawer] WebSocket으로 수량 변경 전송:', { itemId, newQuantity });
        wsService.updateCartQuantity(itemId, newQuantity);
      } else {
        console.log('[CartDrawer] WebSocket 연결 안됨');
      }
    }
    
    // BroadcastChannel을 통해 다른 탭에 알림
    try {
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.postMessage({ type: 'cart_updated' });
      }
    } catch (error) {
      console.log('Failed to broadcast cart update:', error);
    }
  };

  // 주문내역 페이지 열기 (더 이상 사용하지 않음)
  // useEffect(() => {
  //   if (shouldOpenOrderHistory && onOrderComplete) {
  //     onOrderComplete();
  //     setShouldOpenOrderHistory(false);
  //   }
  // }, [shouldOpenOrderHistory, onOrderComplete]);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(false);
      setIsVisible(true);
      
      // 주문 완료 화면 상태 초기화
      setIsOrderCompletedOpen(false);
      setIsPhoneInputOpen(false);
      setIsOrdering(false);
      
      // body 스크롤 방지
      lockScroll();
      
      // 모달 열림 시 body에 클래스 추가
      document.body.classList.add('modal-open');
      
      window.history.pushState({ cartDrawer: true }, '');
      
      setTimeout(() => {
        setIsAnimating(true);
      }, 10);
      
      // 데이터 새로고침
      loadCartData();
    } else {
      setIsAnimating(false);
      setIsReviewBenefitsOpen(false);

      const timer = setTimeout(() => {
        setIsVisible(false);
        // 애니메이션이 완료된 후 body 스크롤 복원
        unlockScroll();
        // 모달 닫힘 시 body 클래스 제거
        document.body.classList.remove('modal-open');
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    const handlePopState = () => {
      // 리뷰 혜택 페이지가 열려있으면 그것만 닫기
      if (isReviewBenefitsOpen) {
        setIsReviewBenefitsOpen(false);
        return;
      }

      // 주문완료 페이지가 열려있으면 아무것도 하지 않음
      if (isOrderCompletedOpen) {
        return;
      }

      // 장바구니가 열려있으면 닫기
      if (isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener('popstate', handlePopState);

      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [isOpen, isReviewBenefitsOpen, isOrderCompletedOpen, onClose]);

  useEffect(() => {
    const scaleUI = () => {
      const wrapper = document.querySelector('.cart-drawer-wrapper');
      if (wrapper) {
        const viewportWidth = window.innerWidth;
        const scale = Math.min(viewportWidth / 360, 1.5);
        wrapper.style.zoom = scale;
      }
    };
    
    if (isVisible) {
      scaleUI();
      window.addEventListener('resize', scaleUI);
    }
    
    return () => {
      window.removeEventListener('resize', scaleUI);
    };
  }, [isVisible]);

  // 장바구니 스크롤 시 배경 스크롤 방지 및 pull-to-refresh 방지
  useEffect(() => {
    if (!isVisible) return;

    const handleWheel = (e) => {
      const cartContent = document.querySelector('.cart-drawer-content');
      if (!cartContent) return;

      const isScrollable = cartContent.scrollHeight > cartContent.clientHeight;
      if (!isScrollable) {
        e.preventDefault();
        return;
      }

      const scrollTop = cartContent.scrollTop;
      const scrollHeight = cartContent.scrollHeight;
      const clientHeight = cartContent.clientHeight;
      const deltaY = e.deltaY;

      // 상단에서 위로 스크롤하려고 할 때
      if (scrollTop === 0 && deltaY < 0) {
        e.preventDefault();
      }
      // 하단에서 아래로 스크롤하려고 할 때
      else if (scrollTop + clientHeight >= scrollHeight - 1 && deltaY > 0) {
        e.preventDefault();
      }
    };

    const handleTouchMove = (e) => {
      // 터치 이벤트가 cart-drawer-content 내부에서 발생한 경우만 허용
      const cartContent = document.querySelector('.cart-drawer-content');
      if (cartContent && cartContent.contains(e.target)) {
        const isScrollable = cartContent.scrollHeight > cartContent.clientHeight;
        if (!isScrollable) {
          e.preventDefault();
          return;
        }
        
        // Pull-to-refresh 방지
        const scrollTop = cartContent.scrollTop;
        if (scrollTop === 0) {
          // 상단에서 아래로 당기는 동작 방지
          const touch = e.touches[0];
          if (!cartContent.dataset.touchStartY) {
            cartContent.dataset.touchStartY = touch.clientY;
          }
          const touchStartY = parseFloat(cartContent.dataset.touchStartY);
          const touchMoveY = touch.clientY;
          
          if (touchMoveY > touchStartY) {
            e.preventDefault();
          }
        }
      } else {
        e.preventDefault();
      }
    };

    const handleTouchStart = (e) => {
      const cartContent = document.querySelector('.cart-drawer-content');
      if (cartContent && e.touches[0]) {
        cartContent.dataset.touchStartY = e.touches[0].clientY;
      }
    };

    const handleTouchEnd = () => {
      const cartContent = document.querySelector('.cart-drawer-content');
      if (cartContent) {
        delete cartContent.dataset.touchStartY;
      }
    };

    const cartDrawer = document.querySelector('.cart-drawer');
    if (cartDrawer) {
      cartDrawer.addEventListener('wheel', handleWheel, { passive: false });
      cartDrawer.addEventListener('touchmove', handleTouchMove, { passive: false });
      cartDrawer.addEventListener('touchstart', handleTouchStart, { passive: true });
      cartDrawer.addEventListener('touchend', handleTouchEnd, { passive: true });
    }

    return () => {
      if (cartDrawer) {
        cartDrawer.removeEventListener('wheel', handleWheel);
        cartDrawer.removeEventListener('touchmove', handleTouchMove);
        cartDrawer.removeEventListener('touchstart', handleTouchStart);
        cartDrawer.removeEventListener('touchend', handleTouchEnd);
      }
    };
  }, [isVisible]);

  // Calculate item total price including options
  const calculateItemTotal = (item) => {
    // 선택 메뉴에서 생성된 항목인 경우 (이미 독립적인 메뉴로 변환됨)
    if (item.isFromChoiceMenu) {
      const price = item.menuItem.price || item.menuItem.basePrice || 0;
      return price * item.quantity;
    }

    // 선택 메뉴 타입 체크 (구버전 호환)
    const isChoiceType = item.menuItem?.menuType === '선택';

    // 선택 메뉴인 경우 선택한 옵션의 가격만 사용 (구버전 호환)
    if (isChoiceType && item.selectedOptions) {
      // 선택된 옵션 찾기
      const selectedOption = Array.isArray(item.selectedOptions)
        ? item.selectedOptions[0]
        : Object.values(item.selectedOptions)[0];

      if (selectedOption && selectedOption.price) {
        const optionPrice = parseInt(selectedOption.price.toString().replace(/[^0-9]/g, '')) || 0;
        return optionPrice * item.quantity;
      }
      return 0;
    }

    // 고정세트 메뉴 체크
    const isFixedSet = item.menuItem?.menuType === '고정세트';

    // 고정세트인 경우 DB에 이미 총 가격이 계산되어 있으므로 옵션 가격을 더하지 않음
    if (isFixedSet) {
      const price = item.menuItem[selectedLanguage]?.price || item.menuItem.ko?.price || item.menuItem.price || '0';
      const basePrice = parseInt((price || '0').toString().replace(/[^0-9]/g, '')) || 0;
      return basePrice * item.quantity;
    }

    // 일반 메뉴 및 변동세트 메뉴의 기존 로직
    const price = item.menuItem[selectedLanguage]?.price || item.menuItem.ko?.price || item.menuItem.price || '0';
    const basePrice = parseInt((price || '0').toString().replace(/[^0-9]/g, '')) || 0;
    let optionsPrice = 0;

    if (item.selectedOptions) {
      // 새 형식 (배열) 체크
      if (Array.isArray(item.menuItem.options)) {
        // 새 메뉴 형식: selectedOptions가 {optionName_index: choice} 형태
        Object.entries(item.selectedOptions).forEach(([key, choice]) => {
          if (choice && choice.price) {
            const price = parseInt(choice.price.toString().replace(/[^0-9]/g, '')) || 0;
            optionsPrice += price;
          }
        });
      }
      // 기존 형식 (객체)
      else if (item.menuItem.options) {
        Object.entries(item.menuItem.options).forEach(([optionTitle, optionData], index) => {
          const selected = item.selectedOptions[index];

          if (optionData.type === 'required' && selected) {
            const option = optionData.items.find(opt => opt.name === selected);
            if (option && option.price) {
              optionsPrice += option.price;
            }
          } else if (optionData.type === 'optional' && Array.isArray(selected)) {
            selected.forEach(optionName => {
              const option = optionData.items.find(opt => opt.name === optionName);
              if (option && option.price) {
                optionsPrice += option.price;
              }
            });
          }
        });
      }
    }

    return (basePrice + optionsPrice) * item.quantity;
  };

  // Price animation effect
  useEffect(() => {
    const totalPrice = cartItems.reduce((sum, item) => {
      return sum + calculateItemTotal(item);
    }, 0);
    
    const newPrice = totalPrice.toLocaleString();
    
    // 장바구니가 비어있을 때는 애니메이션 없이 바로 0으로 설정
    if (cartItems.length === 0) {
      setDisplayPrice('0');
      setPrevPrice('0');
      return;
    }
    
    // 처음 로드될 때 (0에서 실제 가격으로)
    if (displayPrice === '0' && newPrice !== '0') {
      // 진행 중인 애니메이션 타이머 취소
      if (priceAnimationTimeoutRef.current) {
        clearTimeout(priceAnimationTimeoutRef.current);
      }
      
      setPrevPrice('0');
      setAnimationKey(prev => prev + 1);
      setIsPriceIncreasing(true);
      setPriceAnimating(true);
      
      setTimeout(() => {
        setDisplayPrice(newPrice);
      }, 100);
      
      priceAnimationTimeoutRef.current = setTimeout(() => {
        setPriceAnimating(false);
      }, 700);
      return;
    }
    
    // 일반적인 가격 변경
    if (newPrice !== displayPrice) {
      // 진행 중인 애니메이션 타이머 취소
      if (priceAnimationTimeoutRef.current) {
        clearTimeout(priceAnimationTimeoutRef.current);
      }
      
      setPrevPrice(displayPrice);
      setAnimationKey(prev => prev + 1);
      
      // 가격 증가/감소 판단
      const prevPriceNum = parseInt(displayPrice.replace(/,/g, '')) || 0;
      const newPriceNum = parseInt(newPrice.replace(/,/g, '')) || 0;
      setIsPriceIncreasing(newPriceNum > prevPriceNum);
      
      setPriceAnimating(true);
      
      setTimeout(() => {
        setDisplayPrice(newPrice);
      }, 50);
      
      priceAnimationTimeoutRef.current = setTimeout(() => {
        setPriceAnimating(false);
      }, 600);
    }
  }, [cartItems, displayPrice, priceAnimating]);
  
  // Quantity animation effect
  useEffect(() => {
    const totalQuantity = cartItems.reduce((sum, item) => sum + item.quantity, 0);
    
    if (totalQuantity !== displayQuantity && !quantityAnimating) {
      setPrevQuantity(displayQuantity);
      setQuantityAnimationKey(prev => prev + 1);
      setIsQuantityIncreasing(totalQuantity > displayQuantity);
      setQuantityAnimating(true);
      
      setTimeout(() => {
        setDisplayQuantity(totalQuantity);
      }, 50);
      
      setTimeout(() => {
        setQuantityAnimating(false);
      }, 300);
    }
  }, [cartItems, displayQuantity, quantityAnimating]);

  if (!isVisible) return null;

  const formatSelectedOptions = (item) => {
    const optionLines = [];

    // 고정세트 메뉴인 경우 옵션을 표시하지 않음
    if (item.menuItem && item.menuItem.menuType === '고정세트') {
      return optionLines; // 빈 배열 반환
    }

    // 메뉴에 hidden: true인 옵션이 있는 경우 (고정세트) 옵션을 표시하지 않음
    if (item.menuItem && Array.isArray(item.menuItem.options)) {
      const hasHiddenOptions = item.menuItem.options.some(opt => opt.hidden === true);
      if (hasHiddenOptions) {
        return optionLines; // 빈 배열 반환
      }
    }
    
    if (item.selectedOptions) {
      // selectedOptions가 배열인 경우 (새로운 형식)
      if (Array.isArray(item.selectedOptions)) {
        // 옵션 그룹별로 정리
        const optionGroups = {};
        
        item.selectedOptions.forEach((option) => {
          const groupId = option.groupId || 'options';
          
          // 그룹 ID로부터 실제 그룹 이름 찾기 (인덱스 제거)
          let groupName = groupId;
          // groupId에서 _숫자 패턴 제거
          const cleanGroupId = groupId.replace(/_\d+$/, '');
          
          if (Array.isArray(item.menuItem.options)) {
            const optionGroup = item.menuItem.options.find(g => {
              const gName = g.name || g.option_group_name;
              return gName === cleanGroupId || gName === groupId ||
                `${gName}_${item.menuItem.options.indexOf(g)}` === groupId;
            });
            if (optionGroup) {
              // 언어별 옵션 그룹 이름 가져오기
              if (selectedLanguage === 'ko') {
                groupName = optionGroup.option_group_name_ko || optionGroup.group_name_ko || optionGroup.name || optionGroup.option_group_name;
              } else if (selectedLanguage === 'en') {
                groupName = optionGroup.option_group_name_en || optionGroup.group_name_en || optionGroup.name || optionGroup.option_group_name;
              } else if (selectedLanguage === 'ja') {
                groupName = optionGroup.option_group_name_ja || optionGroup.group_name_ja || optionGroup.name || optionGroup.option_group_name;
              } else if (selectedLanguage === 'zh') {
                groupName = optionGroup.option_group_name_zh || optionGroup.group_name_zh || optionGroup.name || optionGroup.option_group_name;
              } else {
                groupName = optionGroup.name || optionGroup.option_group_name;
              }
            } else {
              // 옵션 그룹을 찾지 못한 경우 cleanGroupId 사용
              groupName = cleanGroupId;
            }
          }
          
          if (!optionGroups[groupName]) {
            optionGroups[groupName] = [];
          }
          
          if (option && (option.name || option.option_name)) {
            // 언어별 옵션 이름 가져오기 (option_name_ko, option_name_en 등)
            let optionName;
            if (selectedLanguage === 'ko') {
              optionName = option.option_name_ko || option.name || option.option_name;
            } else if (selectedLanguage === 'en') {
              optionName = option.option_name_en || option.name || option.option_name;
            } else if (selectedLanguage === 'ja') {
              optionName = option.option_name_ja || option.name || option.option_name;
            } else if (selectedLanguage === 'zh') {
              optionName = option.option_name_zh || option.name || option.option_name;
            } else {
              optionName = option.name || option.option_name;
            }
            if (option.price && parseInt(option.price) > 0) {
              const formattedOption = selectedLanguage === 'ko' 
                ? `${optionName} (+${parseInt(option.price).toLocaleString()}원)`
                : `${optionName} (+₩${parseInt(option.price).toLocaleString()})`;
              optionGroups[groupName].push(formattedOption);
            } else {
              optionGroups[groupName].push(optionName);
            }
          }
        });
        
        // 각 그룹의 옵션들을 라인으로 추가
        Object.entries(optionGroups).forEach(([groupName, options]) => {
          if (options.length > 0) {
            optionLines.push({
              title: groupName,
              content: options.join(' / ')
            });
          }
        });
      }
      // 기존 형식 (객체)
      else if (Array.isArray(item.menuItem.options)) {
        // 새 메뉴 형식이지만 selectedOptions가 객체인 경우
        const optionGroups = {};

        Object.entries(item.selectedOptions).forEach(([key, choice]) => {
          // key에서 _숫자 패턴 제거하여 순수한 그룹 이름만 추출
          const cleanGroupId = key.replace(/_\d+$/, '');

          // 실제 옵션 그룹 찾아서 번역된 이름 가져오기
          let groupName = cleanGroupId;
          const optionGroup = item.menuItem.options.find(g => {
            const gName = g.name || g.option_group_name;
            return gName === cleanGroupId || gName === key ||
              `${gName}_${item.menuItem.options.indexOf(g)}` === key;
          });
          if (optionGroup) {
            if (selectedLanguage === 'ko') {
              groupName = optionGroup.option_group_name_ko || optionGroup.group_name_ko || optionGroup.name || optionGroup.option_group_name;
            } else if (selectedLanguage === 'en') {
              groupName = optionGroup.option_group_name_en || optionGroup.group_name_en || optionGroup.name || optionGroup.option_group_name;
            } else if (selectedLanguage === 'ja') {
              groupName = optionGroup.option_group_name_ja || optionGroup.group_name_ja || optionGroup.name || optionGroup.option_group_name;
            } else if (selectedLanguage === 'zh') {
              groupName = optionGroup.option_group_name_zh || optionGroup.group_name_zh || optionGroup.name || optionGroup.option_group_name;
            } else {
              groupName = optionGroup.name || optionGroup.option_group_name;
            }
          }

          if (!optionGroups[groupName]) {
            optionGroups[groupName] = [];
          }
          if (choice && (choice.name || choice.option_name)) {
            // 언어별 옵션 이름 가져오기
            let choiceName;
            if (selectedLanguage === 'ko') {
              choiceName = choice.option_name_ko || choice.name || choice.option_name;
            } else if (selectedLanguage === 'en') {
              choiceName = choice.option_name_en || choice.name || choice.option_name;
            } else if (selectedLanguage === 'ja') {
              choiceName = choice.option_name_ja || choice.name || choice.option_name;
            } else if (selectedLanguage === 'zh') {
              choiceName = choice.option_name_zh || choice.name || choice.option_name;
            } else {
              choiceName = choice.name || choice.option_name;
            }
            if (choice.price && parseInt(choice.price) > 0) {
              const formattedOption = selectedLanguage === 'ko'
                ? `${choiceName} (+${parseInt(choice.price).toLocaleString()}원)`
                : `${choiceName} (+₩${parseInt(choice.price).toLocaleString()})`;
              optionGroups[groupName].push(formattedOption);
            } else {
              optionGroups[groupName].push(choiceName);
            }
          }
        });
        
        // 각 그룹의 옵션들을 라인으로 추가
        Object.entries(optionGroups).forEach(([groupName, options]) => {
          if (options.length > 0) {
            optionLines.push({
              title: groupName,
              content: options.join(' / ')
            });
          }
        });
      }
      // 기존 형식 (객체)
      else if (item.menuItem.options) {
        Object.entries(item.menuItem.options).forEach(([optionTitle, optionData], index) => {
          const selected = item.selectedOptions[index];
          
          if (selected) {
            if (Array.isArray(selected)) {
              // 다중 선택 옵션 (사이드메뉴 추가 등)
              const formattedItems = selected.map(optionName => {
                const optionInfo = optionData.items.find(opt => opt.name === optionName);
                if (optionInfo && optionInfo.price > 0) {
                  return selectedLanguage === 'ko' 
                    ? `${optionName} (${optionInfo.price.toLocaleString()}원)`
                    : `${optionName} (₩${optionInfo.price.toLocaleString()})`;
                }
              return optionName;
            });
            optionLines.push({
              title: optionTitle,
              content: formattedItems.join(' / ')
            });
          } else {
            // 단일 선택 옵션
            const optionInfo = optionData.items.find(opt => opt.name === selected);
            let content = selected;
            if (optionInfo && optionInfo.price > 0) {
              content = selectedLanguage === 'ko'
                ? `${selected} (${optionInfo.price.toLocaleString()}원)`
                : `${selected} (₩${optionInfo.price.toLocaleString()})`;
            }
            optionLines.push({
              title: optionTitle,
              content: content
            });
          }
        }
        });
      }
    }
    
    return optionLines;
  };

  const CartItem = ({ item }) => {
    const handleQuantityChange = (newQuantity) => {
      updateQuantity(item.id, newQuantity);
    };

    return (
      <div className="cart-item-wrapper">
        <div className="cart-item-content-box">
          <div className="cart-item-info">
            <h3 className="cart-item-name">{(() => {
              // 언어별 이름 처리 로직
              const langName = item.menuItem[`name_${selectedLanguage}`] ||
                              item.menuItem[selectedLanguage]?.name ||
                              item.menuItem.ko?.name ||
                              item.menuItem.name_ko ||
                              item.menuItem.menu_name ||
                              item.menuItem.name;
              console.log('Cart item display:', {
                selectedLanguage,
                menuItem: item.menuItem,
                displayName: langName
              });
              return langName;
            })()}</h3>
            <div className="item-unit-price">
              {t.price} : {(() => {
                const priceStr = item.menuItem[selectedLanguage]?.price || item.menuItem.ko?.price || item.menuItem.price || '0';
                // 숫자 추출 및 포맷팅
                const price = typeof priceStr === 'number' ? priceStr : parseInt(priceStr.toString().replace(/[^0-9]/g, '')) || 0;
                // 한국어가 아닌 경우 ₩ 기호 사용
                return selectedLanguage === 'ko' ? `${price.toLocaleString()}원` : `₩${price.toLocaleString()}`;
              })()}
            </div>
            <div className="cart-item-details">
              {formatSelectedOptions(item).map((option, index) => (
                <div key={index} className="option-line">
                  <span className="option-prefix">ㄴ</span>
                  <span className="option-content">
                    {option.title} : {option.content}
                  </span>
                </div>
              ))}
            </div>
            <p className="cart-item-total">
              {selectedLanguage === 'ko' 
                ? `${calculateItemTotal(item).toLocaleString()}원`
                : `₩${calculateItemTotal(item).toLocaleString()}`
              }
            </p>
          </div>
          
          {item.menuItem.image && (
            <div className="cart-item-image">
              <img src={item.menuItem.image} alt={item.menuItem[selectedLanguage]?.name || item.menuItem.ko?.name || item.menuItem.name} />
            </div>
          )}
          
          <div className="cart-quantity-selector">
            <button 
              className="cart-quantity-button minus" style={{ transform: 'translateY(-1px)' }}
              onClick={() => {
                // 진동 피드백
                if ('vibrate' in navigator) {
                  navigator.vibrate(20);
                }
                if (item.quantity === 1) {
                  updateQuantity(item.id, 0);
                } else {
                  handleQuantityChange(item.quantity - 1);
                }
              }}
            >
              {item.quantity === 1 ? (
                <img src={trashIcon} alt="삭제" style={{ width: '14px', height: '14px'}} />
              ) : (
                <svg width="11" height="2" viewBox="0 0 11 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect y="0.388916" width="11" height="1.22222" rx="0.611111" fill="#000000"/>
                </svg>
              )}
            </button>
            <div className="cart-quantity-display">
              <div className="cart-quantity-text-container">
                <div className="cart-quantity-text">
                  {item.quantity}
                </div>
              </div>
            </div>
            <button 
              className="cart-quantity-button plus"
              onClick={() => {
                // 진동 피드백
                if ('vibrate' in navigator) {
                  navigator.vibrate(20);
                }
                handleQuantityChange(item.quantity + 1);
              }}
            >
              <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg" style={{transform: 'translateY(-1px)' }}>
                <rect y="4.88892" width="11" height="1.22222" rx="0.611111" fill="#000000"/>
                <rect x="6.11108" y="0" width="11" height="1.22222" rx="0.611111" transform="rotate(90 6.11108 0)" fill="#000000"/>
              </svg>
            </button>
          </div>
          
        </div>
      </div>
    );
  };

  // Use already calculated values from hooks
  const totalPrice = cartItems.reduce((sum, item) => sum + calculateItemTotal(item), 0);
  const totalQuantity = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className={`cart-drawer ${isAnimating ? 'open' : ''}`}>
      <div className="cart-drawer-wrapper">
        {!isOrderCompletedOpen && (
          <div className="cart-drawer-content">
            <div className="back-button-container">
              <button className="back-button" onClick={onClose}>
                <svg width="27" height="27" viewBox="0 0 27 27" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5.625 13.5H21.375M5.625 13.5L12.375 20.25M5.625 13.5L12.375 6.75" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            
            {cartItems.length > 0 && (
              <div className="cart-header-icon-section">
                <div className="cart-icon-wrapper">
                  <img src={cartIcon} alt="장바구니" />
                </div>
                <h2 className="cart-title">{t.cartTitle}</h2>
              </div>
            )}
            
            {cartItems.length > 0 ? (
              <div className="cart-section">
                <div className="cart-items-container">
                  {cartItems.map(item => (
                    <div
                      key={item.id}
                      className={`cart-item-animation-wrapper ${removingItems.has(item.id) ? 'removing' : ''}`}
                    >
                      <CartItem item={item} />
                    </div>
                  ))}
                </div>
              </div>
            ) : isOrdering ? null : (
              <div className="empty-cart">
                <img 
                  src={cartNotActiveIcon}
                  alt="빈 장바구니" 
                  className="empty-cart-icon"
                />
                <p className="empty-cart-text">{t.emptyCartText}</p>
                <button className="add-menu-button" 
                  onMouseDown={(e) => e.currentTarget.classList.add('pressed')}
                  onMouseUp={(e) => {
                    e.currentTarget.classList.remove('pressed');
                    // 버튼이 원래 크기로 돌아온 후 페이지 전환
                    setTimeout(() => {
                      if (onRequestScrollToCategories) {
                        onRequestScrollToCategories();
                      }
                      onClose();
                    }, 100);
                  }}
                  onTouchStart={(e) => e.currentTarget.classList.add('pressed')}
                  onTouchEnd={(e) => {
                    e.currentTarget.classList.remove('pressed');
                    // 버튼이 원래 크기로 돌아온 후 페이지 전환
                    setTimeout(() => {
                      if (onRequestScrollToCategories) {
                        onRequestScrollToCategories();
                      }
                      onClose();
                    }, 100);
                  }}
                >
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect y="4.5" width="10" height="1" rx="0.5" fill="#000000"/>
                    <rect x="5.5" y="0" width="10" height="1" rx="0.5" transform="rotate(90 5.5 0)" fill="#000000"/>
                  </svg>
                  <span>{t.addMenu}</span>
                </button>
              </div>
            )}
          </div>
        )}
        
        {cartItems.length > 0 && !isOrderCompletedOpen && (
          <div className="cart-fixed-bottom">
            <button className="cart-bottom-button" disabled={isOrdering} onClick={async (e) => {
              // 데모: 리뷰 유도 없이 바로 주문 완료.
              if (IS_DEMO) {
                handleDirectOrder();
                return;
              }
              // 시뮬레이션: 좌측 20% 클릭 → 리뷰 주문 / 우측 80% → 바로 주문 (영업 시연용).
              if (isSimulation) {
                const rect = e.currentTarget.getBoundingClientRect();
                const clickX = e.clientX - rect.left;
                if (clickX < rect.width * 0.2) {
                  setIsReviewBenefitsOpen(true);
                  window.history.pushState({ reviewBenefits: true }, '');
                } else {
                  handleDirectOrder();
                }
                return;
              }

              // 리뷰 기능이 활성화되어 있고 한국어를 선택한 경우만 리뷰 페이지 표시
              if (reviewEnabled && selectedLanguage === 'ko') {
                // props로 받은 hasExistingOrder가 있으면 사용, 없으면 API 호출
                let hasOrder = propHasExistingOrder;

                // props 값이 명확하지 않으면 API로 다시 체크
                if (propHasExistingOrder === false || propHasExistingOrder === undefined) {
                  console.log('[주문 체크] props 값이 없어서 API 호출');
                  let orderCheck;
                  if (posType === 'okdc') {
                    orderCheck = await checkTableOrderExistsOKDC(storeId, tableCode);
                    hasOrder = orderCheck.hasOrder;
                  } else if (posType === 'wowpos') {
                    // 와우포스: PC 메모리 _tableOrders 에 누적 주문이 있는지로 첫 주문/추가 주문 판별
                    try {
                      const wp = await fetchWowposOrderHistory(storeId, tableCode);
                      hasOrder = Array.isArray(wp.orders) && wp.orders.length > 0;
                      console.log('[주문 체크] WowPos 결과 — 주문 누적 개수:', wp.orders?.length || 0);
                    } catch (wpErr) {
                      console.warn('[주문 체크] WowPos 조회 실패 — 첫 주문 가정:', wpErr.message);
                      hasOrder = false;
                    }
                  } else {
                    orderCheck = await checkTableOrderExistsUnion(tableCode);
                    hasOrder = orderCheck.hasOrder;
                  }
                  console.log('[주문 체크] hasOrder:', hasOrder);
                } else {
                  console.log('[주문 체크] props 값 사용:', propHasExistingOrder);
                }

                if (!hasOrder) {
                  // 첫 주문인 경우에만 리뷰 혜택 페이지 표시
                  console.log('첫 주문이므로 리뷰 혜택 페이지 표시');
                  setIsReviewBenefitsOpen(true);
                  window.history.pushState({ reviewBenefits: true }, '');
                } else {
                  // 재주문인 경우 바로 주문 완료
                  console.log('재주문이므로 바로 주문 진행');
                  handleDirectOrder();
                }
              } else {
                // 리뷰 기능이 꺼져 있거나 한국어가 아닌 경우 바로 주문 완료
                handleDirectOrder();
              }
            }} style={{ gap: selectedLanguage === 'en' ? '4px' : '8px' }}>
              <div className="menu-count-badge">
                {quantityAnimating ? (
                  <div className="quantity-animation-container">
                    <span 
                      className={`quantity-digit ${isQuantityIncreasing ? 'quantity-exit-up' : 'quantity-exit-down'}`}
                      key={`prev-${quantityAnimationKey}`}
                    >
                      {prevQuantity}
                    </span>
                    <span 
                      className={`quantity-digit ${isQuantityIncreasing ? 'quantity-enter-up' : 'quantity-enter-down'}`}
                      key={`current-${quantityAnimationKey}`}
                    >
                      {displayQuantity}
                    </span>
                  </div>
                ) : (
                  <span className="quantity-digit">{displayQuantity}</span>
                )}
              </div>
              {/* 영어일 때 Order 텍스트 표시 */}
              {selectedLanguage === 'en' && (
                <span>{t.order}</span>
              )}
              <div className="price-slot-container">
                {/* 해외 언어일 때 ₩ 표시 */}
                {selectedLanguage !== 'ko' && (
                  <span className="price-prefix">₩</span>
                )}
                {(() => {
                  // 가격 포맷팅 함수
                  const formatPriceForAnimation = (price) => {
                    const numStr = price.replace(/,/g, '');
                    const result = [];
                    for (let i = 0; i < numStr.length; i++) {
                      if (i > 0 && (numStr.length - i) % 3 === 0) {
                        result.push(',');
                      }
                      result.push(numStr[i]);
                    }
                    return result;
                  };
                  
                  const currentPriceArray = formatPriceForAnimation(displayPrice);
                  const prevPriceArray = formatPriceForAnimation(prevPrice);
                  
                  // 더 긴 길이에 맞춰 정렬
                  const maxLength = Math.max(currentPriceArray.length, prevPriceArray.length);
                  const alignedCurrent = Array(maxLength).fill(' ');
                  const alignedPrev = Array(maxLength).fill(' ');
                  
                  // 뒤에서부터 채우기
                  for (let i = 0; i < currentPriceArray.length; i++) {
                    alignedCurrent[maxLength - currentPriceArray.length + i] = currentPriceArray[i];
                  }
                  for (let i = 0; i < prevPriceArray.length; i++) {
                    alignedPrev[maxLength - prevPriceArray.length + i] = prevPriceArray[i];
                  }
                  
                  return alignedCurrent.map((digit, index) => {
                    const prevDigit = alignedPrev[index];
                    const isNewSlot = prevDigit === ' ' && digit !== ' ';
                    const isRemovedSlot = prevDigit !== ' ' && digit === ' ';
                    const isChanged = priceAnimating && prevDigit !== digit && !isNewSlot && !isRemovedSlot;
                    
                    // 쉼표는 애니메이션 없이 처리
                    if (digit === ',') {
                      return (
                        <div key={`${index}-${animationKey}-comma`} className="digit-container comma">
                          <span className={`digit ${isNewSlot ? 'digit-fade-in' : ''}`}>,</span>
                        </div>
                      );
                    }
                    
                    // 빈 공간은 렌더링하지 않음
                    if (digit === ' ' && !isNewSlot && !priceAnimating) {
                      return null;
                    }
                    
                    // 중간 숫자 생성
                    const generateSmoothIntermediates = () => {
                      if (!isChanged || isNaN(prevDigit) || isNaN(digit) || prevDigit === ' ' || digit === ' ') {
                        return [];
                      }
                      
                      const from = parseInt(prevDigit);
                      const to = parseInt(digit);
                      const diff = Math.abs(to - from);
                      
                      if (diff >= 5) {
                        const intermediates = [];
                        if (isPriceIncreasing) {
                          for (let i = 0; i < 3; i++) {
                            intermediates.push((from + i + 1) % 10);
                          }
                        } else {
                          for (let i = 0; i < 3; i++) {
                            intermediates.push((from - i - 1 + 10) % 10);
                          }
                        }
                        return intermediates;
                      }
                      
                      const intermediates = [];
                      if (from < to) {
                        for (let i = from + 1; i < to; i++) {
                          intermediates.push(i);
                        }
                      } else {
                        for (let i = from - 1; i > to; i--) {
                          intermediates.push(i);
                        }
                      }
                      return intermediates;
                    };
                    
                    const intermediateDigits = generateSmoothIntermediates();
                    const animationDelay = index * 30;
                    
                    return (
                      <div key={`${index}-${animationKey}`} className={`digit-container ${isNewSlot ? 'slot-appearing' : ''} ${isRemovedSlot ? 'slot-disappearing' : ''}`}>
                        {isChanged && prevDigit !== ' ' && (
                          <span 
                            className={`digit ${isPriceIncreasing ? 'digit-exit-up' : 'digit-exit'}`}
                            style={{ animationDelay: `${animationDelay}ms` }}
                          >
                            {prevDigit}
                          </span>
                        )}
                        
                        {intermediateDigits.map((intermediateDigit, i) => (
                          <span
                            key={`intermediate-${i}`}
                            className={`digit ${isPriceIncreasing ? 'digit-intermediate-up' : 'digit-intermediate'}`}
                            style={{ 
                              animationDelay: `${animationDelay + (i + 1) * 50}ms`,
                              animationDuration: '50ms'
                            }}
                          >
                            {intermediateDigit}
                          </span>
                        ))}
                        
                        {digit !== ' ' && (
                          <span 
                            className={`digit ${isChanged ? (isPriceIncreasing ? 'digit-enter-up' : 'digit-enter') : ''} ${isNewSlot ? 'digit-fade-in' : ''}`}
                            style={isChanged ? { animationDelay: `${animationDelay + (intermediateDigits.length * 50) + 50}ms` } : isNewSlot ? { animationDelay: `${animationDelay}ms` } : {}}
                          >
                            {digit}
                          </span>
                        )}
                      </div>
                    );
                  }).filter(Boolean);
                })()}
                {/* 한국어일 때 원 표시 */}
                {selectedLanguage === 'ko' && (
                  <span className="price-suffix">원 주문하기</span>
                )}
                {/* 일본어일 때 を注文する 표시 */}
                {selectedLanguage === 'ja' && (
                  <span className="price-suffix">{t.order}</span>
                )}
                {/* 중국어일 때 下单 표시 */}
                {selectedLanguage === 'zh' && (
                  <span className="price-suffix"> {t.order}</span>
                )}
              </div>
            </button>
          </div>
        )}
      </div>

      <style jsx="true">{`
        .cart-drawer {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: white;
          z-index: 1000;
          transform: translateX(100%);
          transition: transform 0.15s cubic-bezier(0.4, 0.0, 0.2, 1);
          overflow: hidden;
          will-change: transform;
          overscroll-behavior: none;
          touch-action: none;
        }

        .cart-drawer.open {
          transform: translateX(0);
        }

        .cart-drawer-wrapper {
          width: 360px;
          height: 100%;
          height: 100vh;
          height: 100dvh;
          margin: 0 auto;
          position: relative;
          display: flex;
          flex-direction: column;
          transform-origin: top center;
          overflow: hidden;
        }
        
        @media screen and (min-width: 361px) {
          .cart-drawer-wrapper {
            zoom: calc(100vw / 360);
          }
        }
        
        @media screen and (min-width: 540px) {
          .cart-drawer-wrapper {
            zoom: 1.5;
          }
        }
        
        @media screen and (max-width: 360px) {
          .cart-drawer-wrapper {
            width: 100vw;
            zoom: calc(100vw / 360);
          }
        }

        .back-button-container {
          position: relative;
          padding: 14px 0 0 0px;
          margin-bottom: -41px;
          z-index: 10;
        }
        
        .back-button {
          background: none;
          border: none;
          padding: 0;
          cursor: pointer;
          width: 27px;
          height: 27px;
          display: block;
        }
        
        .back-button svg {
          width: 100%;
          height: 100%;
          display: block;
        }
        
        .cart-header-icon-section {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 14px 0 20px;
        }
        
        .cart-icon-wrapper {
          width: 74px;
          height: 74px;
        }
        
        .cart-icon-wrapper img {
          width: 100%;
          height: 100%;
        }
        
        .cart-title {
          font-size: 18px;
          font-weight: 600;
          margin: 0;
          color: #000000;
        }

        .cart-drawer-content {
          flex: 1;
          overflow-y: auto;
          scrollbar-width: none;
          -ms-overflow-style: none;
          overscroll-behavior: none;
          overscroll-behavior-y: none;
          -webkit-overflow-scrolling: touch;
          padding: 0 16px 20px;
          min-height: 0;
          touch-action: pan-y;
        }
        
        .cart-drawer-content::-webkit-scrollbar {
          display: none;
        }

        .cart-section {
          margin-bottom: 200px;
        }

        .cart-section-title {
          font-size: 16px;
          font-weight: 700;
          margin: 0 0 16px 0;
          color: #000000;
        }

        .cart-items-container {
          position: relative;
          background: #FFFFFF;
          border: 1px solid #E0E0E0;
          border-radius: 10px;
        }

        .cart-item-wrapper {
          position: relative;
          margin-bottom: 0;
          border-bottom: 1px solid #E0E0E0;
          padding: 20px 20px 56px;
        }

        .cart-item-wrapper:last-child {
          border-bottom: none;
        }

        .cart-item-animation-wrapper:last-child {
          border-bottom: none;
        }
        
        .cart-item-animation-wrapper {
          overflow: hidden;
          transition: all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1);
          transform-origin: center center;
          border-bottom: 1px solid #E0E0E0;
          
        }
        
        .cart-item-animation-wrapper.removing {
          animation: slideOutLeft 0.18s cubic-bezier(0.4, 0.0, 0.2, 1) forwards;
          margin-bottom: -176px; /* Height of cart item to collapse space */
          transition: margin-bottom 0.18s cubic-bezier(0.4, 0.0, 0.2, 1) 0.09s;
          background-color:rgb(255, 255, 255);
        }
        
        @keyframes slideOutLeft {
          0% {
            transform: translateX(0);
            opacity: 1;
          }
          100% {
            transform: translateX(-100%);
            opacity: 0;
          }
        }


        .cart-item-content-box {
          display: flex;
          position: relative;
          flex: 1;
        }
          
        .cart-item-info {
          flex: 1;
          padding-right: 55px;
        }

        .cart-item-name {
          font-size: 18px;
          font-weight: 700;
          margin: 0 0 5px 0;
          color: #000000;
        }

        .item-unit-price {
          font-size: 13px;
          font-weight: 400;
          color: #949494;
          line-height: 20px;
        }

        .cart-item-details {
          margin: 0 0 12px 0;
        }

        .option-line {
          display: flex;
          gap: 4px;
        }

        .option-prefix {
          flex-shrink: 0;
          color: #949494;
          font-size: 13px;
          font-weight: 500;
          letter-spacing: -0.02em;
          line-height: 20px;
        }

        .option-content {
          flex: 1;
          color: #949494;
          font-size: 13px;
          font-weight: 400;
          word-break: keep-all;
          letter-spacing: -0.02em;
          line-height: 20px;
        }

        .cart-item-total {
          font-size: 16px;
          font-weight: 600;
          color: #000000;
          margin: 0 0 4px 0;
        }

        .cart-item-image {
          position: absolute;
          right :0px;
          width: 54px;
          height: 54px;
          border-radius: 6px;
          border: 1px solid #EEEEEE;
          overflow: hidden;
        }

        .cart-item-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }


        .cart-quantity-selector {
          position: absolute;
          bottom: -40px;
          right: 0;
          display: flex;
          align-items: center;
          width: 105px;
          height: 36px;
          background-color: #FFFFFF;
          border: 1px solid #D9D9D9;
          border-radius: 4px;
          overflow: hidden;
        }
        
        .cart-quantity-button {
          position: absolute;
          top: 0;
          width: 32px;
          height: 36px;
          border: none;
          background-color: transparent;
          cursor: pointer;
          z-index: 2;
          padding: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background-color 0.1s ease;
        }
        
        .cart-quantity-button:active {
          background-color: rgba(0, 0, 0, 0.1);
        }
        
        .cart-quantity-button.minus {
          left: 0;
        }
        
        .cart-quantity-button.plus {
          right: 0;
        }
        
        
        .cart-quantity-display {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          font-weight: 500;
          color: #000000;
          user-select: none;
          position: relative;
          overflow: hidden;
          width: 100px;
          height: 36px;
        }
        
        .cart-quantity-text-container {
          position: relative;
          height: 20px;
          width: 40px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }
        
        .cart-quantity-text {
          position: absolute;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: none;
          white-space: nowrap;
          font-size: 14px;
          font-weight: 600;
          width: 100%;
        }
        
        /* 증가 애니메이션 (up): 이전 숫자는 위로, 새 숫자는 아래에서 */
        .cart-quantity-text.current-up {
          animation: slideInFromBottom 0.15s ease-out;
          z-index: 2;
        }
        
        .cart-quantity-text.prev-up {
          animation: slideOutToTop 0.15s ease-out forwards;
          z-index: 1;
          pointer-events: none;
        }
        
        /* 감소 애니메이션 (down): 이전 숫자는 아래로, 새 숫자는 위에서 */
        .cart-quantity-text.current-down {
          animation: slideInFromTop 0.15s ease-out;
          z-index: 2;
        }
        
        .cart-quantity-text.prev-down {
          animation: slideOutToBottom 0.15s ease-out forwards;
          z-index: 1;
          pointer-events: none;
        }
        
        @keyframes slideInFromTop {
          from {
            transform: translateY(-20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes slideOutToBottom {
          from {
            transform: translateY(0);
            opacity: 1;
          }
          to {
            transform: translateY(20px);
            opacity: 0;
            visibility: hidden;
          }
        }
        
        @keyframes slideInFromBottom {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes slideOutToTop {
          from {
            transform: translateY(0);
            opacity: 1;
          }
          to {
            transform: translateY(-20px);
            opacity: 0;
            visibility: hidden;
          }
        }

        

        .add-menu-button:hover {
          background: #EEEEEE;
        }
        
        .add-menu-button.empty {
          width: 200px;
          border: 1px dashed #CCCCCC;
          border-radius: 6px;
          background: #dbdbdb;
          margin: 0;
        }

        .empty-cart {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 400px;
          text-align: center;
          margin-top: 90px;
        }

    
        .cart-fixed-bottom {
          background-image: url(${bottomNavBg});
          position: fixed;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 360px;
          height: 100px;
          display: flex;
          justify-content: center;
          align-items: flex-end;
          padding-bottom: 25px;
          z-index: 1000;
          pointer-events: none;
        }

        .cart-bottom-button {
          position: absolute;
          bottom: 0%;
          margin-bottom: 11px;
          width: 328px;
          height: 50px;
          background-color: #fdcd27;
          border-radius: 10px;
          border: none;
          font-size: 17px;
          font-weight: 700;
          color: #ffffff;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          letter-spacing: -0.32px;
          line-height: 19px;
          transition: transform 0.12s cubic-bezier(0.4, 0.0, 0.2, 1), box-shadow 0.12s ease;
          pointer-events: auto;
        }

        .cart-bottom-button:active {
          transform: scale(0.92);
        }
        
        .menu-count-badge {
          background-color: #ffffff;
          color: #fdcd27;
          min-width: 22px;
          height: 22px;
          padding: 0 6px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 15px;
          font-weight: 700;
          line-height: 18px;
          position: relative;
          overflow: hidden;
        }
        
        /* 수량 애니메이션 스타일 */
        .quantity-animation-container {
          position: relative;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .quantity-digit {
          position: absolute;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
        }
        
        .quantity-exit-up {
          animation: quantitySlideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
        
        .quantity-exit-down {
          animation: quantitySlideDown 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
        
        .quantity-enter-up {
          animation: quantitySlideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
        
        .quantity-enter-down {
          animation: quantitySlideInDown 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
        
        @keyframes quantitySlideUp {
          from {
            transform: translateY(0);
            opacity: 1;
          }
          to {
            transform: translateY(-100%);
            opacity: 0;
          }
        }
        
        @keyframes quantitySlideDown {
          from {
            transform: translateY(0);
            opacity: 1;
          }
          to {
            transform: translateY(100%);
            opacity: 0;
          }
        }
        
        @keyframes quantitySlideInUp {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes quantitySlideInDown {
          from {
            transform: translateY(-100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        /* 가격 룰렛 애니메이션 스타일 */
        .price-slot-container {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0;
          overflow: visible;
          height: 19px;
          position: relative;
          padding-right: 2px;
        }
        
        .digit-container {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          height: 19px;
          min-width: 10px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .digit-container.comma {
          min-width: 3px;
          margin: 0;
        }
        
        .digit-container.slot-appearing {
          animation: slotAppear 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
        
        .digit-container.slot-disappearing {
          animation: slotDisappear 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
          min-width: 0;
          width: 0;
          margin: 0;
        }
        
        .digit {
          display: inline-block;
          line-height: 19px;
          transition: none;
          font-size: 17px;
          font-weight: 700;
          color: #ffffff;
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
        }
        
        .digit-fade-in {
          animation: digitFadeIn 0.3s ease-out both;
        }
        
        .digit-exit {
          position: absolute;
          animation: slideOutBottom 0.3s cubic-bezier(0.4, 0, 0.2, 1) both;
        }
        
        .digit-exit-up {
          position: absolute;
          animation: slideOutTop 0.3s cubic-bezier(0.4, 0, 0.2, 1) both;
        }
        
        .digit-enter {
          animation: slideInTop 0.3s cubic-bezier(0.4, 0, 0.2, 1) both;
        }
        
        .digit-enter-up {
          animation: slideInBottom 0.3s cubic-bezier(0.4, 0, 0.2, 1) both;
        }
        
        .digit-intermediate {
          position: absolute;
          animation: slideIntermediateQuick 100ms linear both;
        }
        
        .digit-intermediate-up {
          position: absolute;
          animation: slideIntermediateQuickUp 100ms linear both;
        }
        
        .price-prefix {
          margin-right: 1px;
          font-size: 17px;
          font-weight: 700;
          color: #ffffff;
        }
        
        .price-suffix {
          margin-left: 4px;
          font-size: 17px;
          font-weight: 700;
          color: #ffffff;
        }
        
        @keyframes slideInTop {
          from {
            transform: translateX(-50%) translateY(-100%);
            opacity: 0;
          }
          to {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes slideOutBottom {
          from {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
          to {
            transform: translateX(-50%) translateY(100%);
            opacity: 0;
          }
        }
        
        @keyframes slideInBottom {
          from {
            transform: translateX(-50%) translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes slideOutTop {
          from {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
          to {
            transform: translateX(-50%) translateY(-100%);
            opacity: 0;
          }
        }
        
        @keyframes slideIntermediateQuick {
          0% {
            transform: translateX(-50%) translateY(-100%);
            opacity: 0.8;
          }
          50% {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
          100% {
            transform: translateX(-50%) translateY(100%);
            opacity: 0;
          }
        }
        
        @keyframes slideIntermediateQuickUp {
          0% {
            transform: translateX(-50%) translateY(100%);
            opacity: 0.8;
          }
          50% {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
          100% {
            transform: translateX(-50%) translateY(-100%);
            opacity: 0;
          }
        }
        
        @keyframes digitFadeIn {
          0% {
            opacity: 0;
            transform: translateX(-50%) scale(0.8);
          }
          100% {
            opacity: 1;
            transform: translateX(-50%) scale(1);
          }
        }
        
        @keyframes slotAppear {
          0% {
            width: 0;
            min-width: 0;
            opacity: 0;
            margin: 0;
          }
          100% {
            width: 10px;
            min-width: 10px;
            opacity: 1;
            margin: 0;
          }
        }
        
        @keyframes slotDisappear {
          0% {
            width: 10px;
            min-width: 10px;
            opacity: 1;
            margin: 0;
          }
          100% {
            width: 0;
            min-width: 0;
            opacity: 0;
            margin: 0;
          }
        }
        
        .empty-cart-icon {
          width: 92px;
          height: 92px;
        }
        
        .empty-cart-text {
          margin-top: 8px;
          margin-bottom: 0;
          font-size: 18px;
          font-weight: 600;
          color: #949494;
        }
        
        .add-menu-button {
          padding: 0 10px;
          width: auto;
          margin-top: 24px;
          height: 31px;
          border: 1px solid #000000;
          border-radius: 20px;
          background: white;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
          cursor: pointer;
          transition: all 0.05s ease;
        }
        
        .add-menu-button.pressed {
          transform: scale(0.92);
          background: #dbdbdb;
        }
        
        .add-menu-button svg {
          width: 10px;
          height: 10px;
        }
        
        .add-menu-button span {
          font-size: 14px;
          font-weight: 400;
          color: #333333;
        }
      `}</style>
      
      <ReviewBenefits
        isOpen={isReviewBenefitsOpen}
        tableCode={tableCode}
        posType={propPosType}
        storeId={propStoreId}
        isSimulation={isSimulation}
        reviewSettings={propReviewSettings}
        onClose={(status, message) => {
          setIsReviewBenefitsOpen(false);
          // 주문 실패로 닫는 경우
          if (status === 'orderFailed') {
            // ReviewBenefits가 슬라이드 애니메이션으로 닫힌 후 장바구니 닫기
            setTimeout(() => {
              setIsAnimating(false);
              setIsVisible(false);
              onClose();
              // 토스트 메시지 표시
              if (onShowToast) {
                const message = selectedLanguage === 'ko' ? '다시 시도해주세요' :
                              selectedLanguage === 'en' ? 'Please try again' :
                              selectedLanguage === 'ja' ? 'もう一度お試しください' :
                              '请重试';
                onShowToast(`<img src="${refuseIcon}" style="width: 12px; height: 12px; vertical-align: middle; margin-right: 8px;" alt="x"/><span style="color: white; font-weight: 600; font-size: 14px;">${message}</span>`);
              }
            }, 350); // ReviewBenefits 슬라이드 애니메이션 시간(300ms) + 약간의 여유
          }
        }}
        selectedLanguage={selectedLanguage}
        totalPrice={totalPrice}
        onConfirm={async (willReview, benefit) => {
          if (isOrdering) return;
          setIsOrdering(true);
          setWillWriteReview(willReview);
          setSelectedBenefit(benefit);

          // 데모(체험) 모드: 혜택 메뉴 포함 허구 주문
          if (IS_DEMO) {
            try {
              let items = [...cartItems];
              if (willReview && benefit && benefit.menuData) {
                items.push({ menuItem: benefit.menuData, quantity: 1, selectedOptions: {} });
              }
              // 사은품 표식은 DEMO_REVIEW_BENEFITS 에 이미 박혀 있어 별도 키 매칭이 필요 없다.
              const order = placeDemoOrder(formatDemoItems(items), { isReview: !!willReview });
              setOrderNumber(order.orderNumber);
              localStorage.removeItem('cartData');
              setCartItems([]);
              setIsReviewBenefitsOpen(false);
              setIsOrderCompletedOpen(true);
            } catch (err) {
              if (onShowToast) onShowToast('주문 처리 중 오류가 발생했습니다', 'error');
            } finally {
              setIsOrdering(false);
            }
            return;
          }

          // 리뷰 혜택은 POS 상품이 아니라 상품코드가 없다 (cartDrawer.jsx 와 동일 처리).
          //   orderItems    — 표시·알림·주문내역용 (혜택 포함)
          //   posOrderItems — POS 전송 전용 (혜택 제외. 와우포스는 POS 연동이 없어 그대로 포함)
          let orderItems = [...cartItems];
          let posOrderItems = [...cartItems];
          // 실패 시 되돌리기 위한 원본 스냅샷 (cartDrawer.jsx 와 동일 이유)
          const cartItemsBeforeOrder = [...cartItems];
          if (willReview && benefit && benefit.menuData) {
            const benefitCartItem = {
              menuItem: benefit.menuData,
              quantity: 1,
              selectedOptions: propPosType === 'okdc' ? [] : {}
            };
            orderItems.push(benefitCartItem);
            if (propPosType === 'wowpos') posOrderItems.push(benefitCartItem);
            setCartItems(orderItems);
          }

          const reviewBenefit = (willReview && benefit && benefit.menuData)
            ? { name: benefit.menuData.ko?.name || benefit.menuData.name || '' }
            : null;

          // POS 타입별 주문 전송 및 주문번호 조회
          let finalOrderNumber = null;
          try {
            if (propPosType === 'wowpos') {
              // WowPos: 사은품 포함 모든 orderItems 를 PC 로 송신. 응답 ack 에 orderNumber.
              // 사은품 식별 — menuData 에 붙여둔 명시 표식만 본다(PC 알림창의 SetNaverReview 표시용).
              // 예전엔 id/menu_code/prod_cd 값 일치로 판별했는데, 사은품 id 와 정규 메뉴 id 가
              // 각각 SERIAL 이라 겹치면 엉뚱한 메뉴가 사은품으로 인쇄됐다.
              const items = orderItems.map((item) => {
                const basePrice = item.menuItem?.basePrice || item.menuItem?.price || 0;
                const qty = item.quantity || 1;
                const isReview = item.menuItem?.is_review_benefit === true;
                // 선택 옵션 추출 (메인 주문 경로와 동일 — 리뷰 혜택 주문에서도 옵션 유지)
                const optList = [];
                const pushOpt = (o) => {
                  if (o && typeof o === 'object' && (o.name || o.ko?.name)) {
                    optList.push({
                      name: o.ko?.name || o.name,
                      qty: o.quantity || 1,
                      price: parseInt(o.option_price || o.additional_price || o.price || 0) || 0,
                    });
                  }
                };
                const so = item.selectedOptions;
                if (Array.isArray(so)) so.forEach(pushOpt);
                else if (so && typeof so === 'object') {
                  Object.values(so).forEach((v) => { if (Array.isArray(v)) v.forEach(pushOpt); else pushOpt(v); });
                }
                const optionsPrice = optList.reduce((s, o) => s + (Number(o.price) || 0) * (Number(o.qty) || 1), 0);
                return {
                  menuId: item.menuItem?.db_id || item.menuItem?.id || null,
                  name: item.menuItem?.ko?.name || item.menuItem?.name || '',
                  price: (basePrice + optionsPrice) * qty,
                  quantity: qty,
                  options: optList,
                  is_review_benefit: isReview,
                };
              });
              const totalAmount = items.reduce((s, i) => s + (Number(i.price) || 0), 0);
              const result = await sendOrderToWowpos({
                storeId: propStoreId,
                tableCode,
                items,
                totalAmount,
              });
              if (result.success) {
                finalOrderNumber = result.orderNumber || null;
              } else {
                throw new Error(result.error || 'WowPos 주문 전송 실패');
              }
            } else if (propPosType === 'okdc') {
              // OKDC POS API로 주문 전송
              console.log('[리뷰 혜택 OKDC 주문] orderItems:', orderItems);
              const okdcOrderData = await formatOrderForOKDC(
                posOrderItems.map(item => ({
                  menuId: item.menuItem.id,
                  prod_cd: item.menuItem.prod_cd || item.menuItem.menu_code,
                  price: item.menuItem.price || item.menuItem.basePrice,
                  quantity: item.quantity,
                  selectedOptions: item.selectedOptions || [],
                  is_taxable: item.menuItem.is_taxable || 'Y'
                })),
                tableCode,
                { storeId: propStoreId }
              );

              const result = await sendOrderToOKDC(okdcOrderData, propStoreId, reviewBenefit);

              if (result.success) {
                // 주문 성공 시 실제 주문번호 조회
                console.log('[리뷰 혜택 OKDC 주문] 주문번호 조회 시작');
                const orderNumberResult = await fetchOKDCTableOrderNumber(propStoreId, tableCode);
                finalOrderNumber = orderNumberResult.orderNumber || okdcOrderData.orderNo;
                console.log('[리뷰 혜택 OKDC 주문] 최종 주문번호:', finalOrderNumber);
              } else {
                console.error('OKDC 주문 전송 실패:', result.message);
                throw new Error(result.message || '주문 전송 실패');
              }
            } else if (propPosType === 'metacity') {
              // METACITY POS API로 주문 전송 (혜택 메뉴 포함 8020)
              const metacityOrderData = formatOrderForMetacity(posOrderItems, tableCode);
              const result = await sendOrderToMetacity(metacityOrderData, propStoreId, reviewBenefit);
              if (result.success) {
                finalOrderNumber = result.orderNumber || null;
              } else {
                console.error('METACITY 주문 전송 실패:', result.message);
                throw new Error(result.message || '주문 전송 실패');
              }
            } else {
              // Union POS API로 주문 전송
              const unionOrderData = formatOrderForUnion(
                posOrderItems.map(item => ({
                  item: item.menuItem,
                  quantity: item.quantity,
                  selectedOptions: item.selectedOptions || {}
                })),
                tableCode,
                selectedLanguage === 'ko' ? 'KO' :
                selectedLanguage === 'en' ? 'EN' :
                selectedLanguage === 'ja' ? 'JA' : 'ZH'
              );


              // 알림용 데이터 구성 (한국어 메뉴명 우선, 수량, 가격, 옵션)
              const notificationItems = orderItems.map(item => {
                const options = [];
                const menuType = item.menuItem?.menuType;
                if (menuType === '고정세트') {
                  if (Array.isArray(item.menuItem?.options)) {
                    item.menuItem.options.forEach(optGroup => {
                      if (optGroup.choices && Array.isArray(optGroup.choices)) {
                        optGroup.choices.forEach(choice => {
                          if (choice.name || choice.ko?.name) {
                            options.push({ name: choice.ko?.name || choice.name, qty: choice.quantity || 1 });
                          }
                        });
                      }
                    });
                  }
                } else if (item.selectedOptions) {
                  if (Array.isArray(item.selectedOptions)) {
                    item.selectedOptions.forEach(opt => {
                      if (opt && opt.name) {
                        options.push({ name: opt.ko?.name || opt.name, qty: opt.quantity || 1 });
                      }
                    });
                  } else if (typeof item.selectedOptions === 'object') {
                    Object.values(item.selectedOptions).forEach(choice => {
                      if (choice && typeof choice === 'object' && choice.name) {
                        options.push({ name: choice.ko?.name || choice.name, qty: choice.quantity || 1 });
                      }
                    });
                  }
                }
                const basePrice = item.menuItem?.ko?.price || item.menuItem?.price || item.menuItem?.basePrice || 0;
                let optionsPrice = 0;
                if (Array.isArray(item.selectedOptions)) {
                  optionsPrice = item.selectedOptions.reduce((sum, opt) => sum + (parseInt(opt?.option_price || opt?.price || 0) || 0), 0);
                } else if (item.selectedOptions && typeof item.selectedOptions === 'object') {
                  optionsPrice = Object.values(item.selectedOptions).reduce((sum, opt) => sum + (parseInt(opt?.option_price || opt?.price || 0) || 0), 0);
                }
                return {
                  name: item.menuItem?.ko?.name || item.menuItem?.name || '',
                  qty: item.quantity || 1,
                  price: (basePrice + optionsPrice) * (item.quantity || 1),
                  is_review_benefit: item.menuItem?.is_review_benefit === true,
                  options
                };
              });
              const notificationTotalPrice = notificationItems.reduce((sum, i) => sum + i.price, 0);

              const result = isSimulation
                ? await simApi.sendOrder({
                    hasOrder: propHasExistingOrder === true,
                    notificationItems,
                    notificationTotalPrice,
                  })
                : await sendOrderToUnion(unionOrderData, notificationItems, notificationTotalPrice);

              if (result.success) {
                // 주문 성공 후 주문 번호 조회 — 시뮬은 스킵
                try {
                  const orderDetail = isSimulation
                    ? { success: true, orderSum: null }
                    : await fetchTableOrderDetail(tableCode);
                  if (orderDetail.success && orderDetail.orderSum) {
                    finalOrderNumber = orderDetail.orderSum.OrderNo;
                    console.log('[리뷰 혜택 Union 주문] 주문 전송 후 주문번호 조회:', finalOrderNumber);
                  } else {
                    finalOrderNumber = 1;
                    console.log('[리뷰 혜택 Union 주문] 주문번호 조회 실패, 기본값 사용');
                  }
                } catch (orderDetailError) {
                  console.error('[리뷰 혜택 Union 주문] 주문번호 조회 오류:', orderDetailError);
                  finalOrderNumber = 1;
                }
              } else {
                console.error('Union 주문 전송 실패:', result.message);
                throw new Error(result.message || '주문 전송 실패');
              }
            }
          } catch (error) {
            console.error('주문 전송 중 오류:', error);
            // 사은품을 넣기 전 상태로 되돌린다 (cartDrawer.jsx 와 동일 이유)
            setCartItems(cartItemsBeforeOrder);
            // 주문 실패 시 에러 표시하고 홈으로 돌아가기
            setIsReviewBenefitsOpen(false);
            setIsOrdering(false);
            setIsAnimating(false);
            setIsVisible(false);
            unlockScroll();
            onClose();
            if (onShowToast) {
              const message = selectedLanguage === 'ko' ? '주문에 실패했습니다. 다시 시도해주세요' :
                            selectedLanguage === 'en' ? 'Order failed. Please try again' :
                            selectedLanguage === 'ja' ? '注文に失敗しました。もう一度お試しください' :
                            '订单失败，请重试';
              onShowToast(`<img src="${refuseIcon}" style="width: 12px; height: 12px; vertical-align: middle; margin-right: 8px;" alt="x"/><span style="color: white; font-weight: 600; font-size: 14px;">${message}</span>`);
            }
            return;
          }

          // 주문 성공 후 주문완료 화면 열기 (리뷰 혜택은 주문완료 화면이 열린 후 닫음)
          setIsOrderCompletedOpen(true);
          setIsReviewBenefitsOpen(false);

          // 주문번호 설정
          setOrderNumber(finalOrderNumber);

          // 네이버 리뷰 약속 등록 (willReview=true 일 때만)
          if (willReview && benefit && benefit.menuData && propStoreId && tableCode) {
            try {
              const benefitPayload = {
                orderNumber: finalOrderNumber || null,
                benefitMenuId: benefit.menuData.id || null,
                benefitMenuName: benefit.menuData.ko?.name || benefit.menuData.name || benefit.menuData.menu_name || null,
                benefitImageUrl: benefit.menuData.image_url || benefit.menuData.imageUrl || null,
              };
              if (isSimulation) {
                await simApi.markFirstVisit(benefitPayload);
              } else {
                await fetch('/api/review/mark-first-visit', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    storeId: propStoreId,
                    tableCode: String(tableCode),
                    ...benefitPayload,
                  })
                });
              }
              console.log('[리뷰 mark 저장] 완료');
            } catch (markErr) {
              console.error('[리뷰 mark 저장] 실패:', markErr);
            }
          }

          // 주문 내역 저장
          const now = new Date();
          const hours = String(now.getHours()).padStart(2, '0');
          const minutes = String(now.getMinutes()).padStart(2, '0');
          const orderTime = `${hours}:${minutes}`;

          const orderData = {
            orderNumber: finalOrderNumber,
            time: orderTime,
            willWriteReview: willReview,
            selectedBenefit: benefit,
            items: orderItems.map(item => {
              const price = item.menuItem[selectedLanguage]?.price || item.menuItem.ko?.price || item.menuItem.price || '0';
              const basePrice = parseInt((price || '0').toString().replace(/[^0-9]/g, '')) || 0;
              let optionsPrice = 0;
              let hasOptions = false;
              
              // 옵션 가격 계산
              if (item.selectedOptions) {
                // 새 형식 (배열) 체크
                if (Array.isArray(item.menuItem.options)) {
                  Object.entries(item.selectedOptions).forEach(([key, choice]) => {
                    if (choice && choice.price) {
                      const price = parseInt(choice.price.toString().replace(/[^0-9]/g, '')) || 0;
                      optionsPrice += price;
                      hasOptions = true;
                    }
                  });
                }
                // 기존 형식 (객체)
                else if (item.menuItem.options) {
                  Object.entries(item.menuItem.options).forEach(([optionTitle, optionData], index) => {
                    const selected = item.selectedOptions[index];
                    
                    if (optionData.type === 'required' && selected) {
                      const option = optionData.items.find(opt => opt.name === selected);
                      if (option && option.price) {
                        optionsPrice += option.price;
                        hasOptions = true;
                      }
                    } else if (optionData.type === 'optional' && Array.isArray(selected)) {
                      selected.forEach(optionName => {
                        const option = optionData.items.find(opt => opt.name === optionName);
                        if (option && option.price) {
                          optionsPrice += option.price;
                          hasOptions = true;
                        }
                      });
                    }
                  });
                }
              }
              
              return {
                name: item.menuItem[selectedLanguage]?.name || item.menuItem.ko?.name || item.menuItem.name,
                // 다국어 메뉴 이름 필드 추가
                name_ko: item.menuItem.name_ko || item.menuItem.menu_name || item.menuItem.name,
                name_en: item.menuItem.name_en || item.menuItem.menu_name || item.menuItem.name,
                name_ja: item.menuItem.name_ja || item.menuItem.menu_name || item.menuItem.name,
                name_zh: item.menuItem.name_zh || item.menuItem.menu_name || item.menuItem.name,
                basePrice: basePrice,
                totalPrice: basePrice + optionsPrice,
                quantity: item.quantity,
                hasOptions: hasOptions,
                selectedOptions: item.selectedOptions,
                options: item.menuItem.options
              };
            })
          };
          
          // 기존 주문 내역 가져오기
          const existingOrders = JSON.parse(localStorage.getItem('orderHistory') || '[]');
          existingOrders.push(orderData);
          // localStorage 사용 중단 - Union API에서만 주문내역 조회
    // localStorage.setItem('orderHistory', JSON.stringify(existingOrders));
          
          // 즉시 장바구니 비우기
          localStorage.removeItem('cartData');
          setCartItems([]);
          
          // BroadcastChannel로 다른 탭에도 알림
          if (broadcastChannelRef.current) {
            broadcastChannelRef.current.postMessage({ type: 'cart-cleared' });
          }
          
          // WebSocket으로 다른 멤버들에게 주문 완료 알림
          if (wsService.isSocketConnected()) {
            wsService.emitOrderCompleted();
          }

          // 사용자가 "주문판으로 돌아가기" 버튼을 누를 때까지 OrderCompleted 화면 유지
        }}
      />
      
      <PhoneNumberInput 
        isOpen={isPhoneInputOpen}
        onClose={() => setIsPhoneInputOpen(false)}
        selectedLanguage={selectedLanguage}
        onConfirm={(phoneNumber) => {
          console.log('Phone number entered:', phoneNumber);
          setIsPhoneInputOpen(false);
        }}
      />
      
      <OrderCompleted
        isOpen={isOrderCompletedOpen}
        orderNumber={orderNumber}
        selectedLanguage={selectedLanguage}
        willWriteReview={willWriteReview}
        onClose={() => {
          // 메뉴판(메인 화면)으로 복귀 — 주문내역 페이지로 가지 않음
          setIsAnimating(false);
          setIsVisible(false);
          setIsOrderCompletedOpen(false);
          document.body.classList.remove('modal-open');
          // 장바구니 닫기 → 메뉴판 노출
          if (onClose) onClose();
        }}
      />
      
      <ReviewGuide 
        isOpen={isReviewGuideOpen}
        onClose={() => {
          // 모든 모달 닫고 메인 화면으로 돌아가기
          setIsReviewGuideOpen(false);
          setIsAnimating(false);
          setIsVisible(false);
          unlockScroll();
          document.body.classList.remove('modal-open');
          if (onClose) {
            onClose();
          }
        }}
      />
    </div>
  );
};

export default CartDrawer;